import AdminUsersPanel from "./components/AdminUsersPanel";
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { BrowserRouter, Routes, Route, Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { Chart as ChartJS, registerables } from "chart.js";
import { Bar as RawBar, Doughnut as RawDoughnut, Line as RawLine } from "react-chartjs-2";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import SubscriptionPlans from "./components/SubscriptionPlans";
import ProtectedPaidFeature from "./components/ProtectedPaidFeature";
import UserStatusCard from "./components/UserStatusCard";
import PaymentSuccessModal from "./components/PaymentSuccessModal";
import AdminEnterpriseLeads from "./components/AdminEnterpriseLeads";
import DashboardStatusCard from "./components/DashboardStatusCard";
ChartJS.register(...registerables);
ChartJS.defaults.devicePixelRatio = 1;

const safeChartData = (data) => {
  const labels = Array.isArray(data?.labels) ? data.labels : [];
  const datasets = Array.isArray(data?.datasets)
    ? data.datasets.map((dataset, index) => ({
        ...dataset,
        label: dataset?.label || `Series ${index + 1}`,
        data: Array.isArray(dataset?.data) ? dataset.data.map((v) => Number.isFinite(Number(v)) ? Number(v) : 0) : [],
      }))
    : [];

  return { labels, datasets };
};

const safeChartKey = (type, data) => {
  const labels = Array.isArray(data?.labels) ? data.labels : [];
  const datasetMeta = Array.isArray(data?.datasets)
    ? data.datasets.map((dataset) => `${dataset?.label || "series"}:${Array.isArray(dataset?.data) ? dataset.data.length : 0}:${Array.isArray(dataset?.data) ? dataset.data.slice(0, 3).join("|") : ""}`)
    : [];
  return `${type}-${labels.length}-${labels.slice(0, 6).join("|")}-${datasetMeta.join("|")}`;
};

function EmptyChartNotice() {
  return (
    <div style={{ minHeight: 240, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontWeight: 700 }}>
      No chart data available
    </div>
  );
}

const makeStableChartOptions = (options = {}) => ({
  ...options,
  responsive: true,
  maintainAspectRatio: false,
  animation: {
    duration: 800,
    easing: "easeInOutQuart",
  },
  plugins: {
    legend: {
      display: true,
      position: "top",
      labels: {
        color: "#e2e8f0",
        font: {
          size: 12,
          weight: "600",
        },
      },
    },
    tooltip: {
      backgroundColor: "#0f172a",
      titleColor: "#fff",
      bodyColor: "#cbd5f5",
      borderColor: "#6366f1",
      borderWidth: 1,
    },
    ...((options?.plugins) || {}),
  },
  scales: {
    x: {
      ticks: {
        color: "#94a3b8",
        font: { size: 11 },
      },
      grid: {
        color: "rgba(148,163,184,0.1)",
      },
    },
    y: {
      ticks: {
        color: "#94a3b8",
        font: { size: 11 },
      },
      grid: {
        color: "rgba(148,163,184,0.1)",
      },
    },
  },
});

function StableChartShell({ children, compact = false }) {
  const shellHeight = compact ? 270 : 360;
  const innerWidth = compact ? 420 : 680;
  return (
    <div style={{ width: "100%", minHeight: shellHeight, height: shellHeight, position: "relative", overflowX: compact ? "hidden" : "auto", overflowY: "hidden" }}>
      <div style={{ minWidth: innerWidth, height: shellHeight }}>
        {children}
      </div>
    </div>
  );
}

function Bar({ data, options, compact = false }) {
  const safeData = safeChartData(data);
  if (!safeData.labels.length || !safeData.datasets.length) return <EmptyChartNotice />;
  return (
    <StableChartShell compact={compact}>
      <RawBar
        key={safeChartKey("bar", safeData)}
        data={safeData}
        options={makeStableChartOptions(options)}
        width={compact ? 420 : 760}
        height={compact ? 250 : 340}
      />
    </StableChartShell>
  );
}

function Doughnut({ data, options, compact = false }) {
  const safeData = safeChartData(data);
  if (!safeData.labels.length || !safeData.datasets.length) return <EmptyChartNotice />;
  return (
    <StableChartShell compact={compact}>
      <RawDoughnut
        key={safeChartKey("doughnut", safeData)}
        data={safeData}
        options={makeStableChartOptions(options)}
        width={compact ? 420 : 760}
        height={compact ? 250 : 340}
      />
    </StableChartShell>
  );
}

function Line({ data, options, compact = false }) {
  const safeData = safeChartData(data);
  if (!safeData.labels.length || !safeData.datasets.length) return <EmptyChartNotice />;
  return (
    <StableChartShell compact={compact}>
      <RawLine
        key={safeChartKey("line", safeData)}
        data={safeData}
        options={makeStableChartOptions(options)}
        width={compact ? 420 : 760}
        height={compact ? 250 : 340}
      />
    </StableChartShell>
  );
}

const API = axios.create({
  baseURL: "https://api.bivoratech.com/api",
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
});

API.interceptors.request.use((req) => {
  const token = localStorage.getItem("token");
  if (token) {
    req.headers.Authorization = `Bearer ${token}`;
  }
  return req;
});

const getErrorMessage = (err, fallback) =>
  err?.response?.data?.message ||
  err?.response?.data?.error ||
  err?.message ||
  fallback;

const COLORS = [
  "#6366f1",
  "#06b6d4",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#14b8a6",
  "#f97316",
  "#84cc16",
  "#e11d48",
];

const HR_TEMPLATE_HEADERS = [
  "Employee_ID",
  "Job_ID",
  "Job_Title",
  "Department",
  "Country",
  "Experience_Level",
  "Years_Experience",
  "Employment_Type",
  "Work_Mode",
  "Salary_USD",
  "Salary",
  "Hire_Date",
  "Exit_Date",
  "Status",
  "Job_Posted_Date",
  "Job_Status",
  "Applicants_Count",
];

const SALES_TEMPLATE_HEADERS = [
  "Order_ID",
  "Order_Date",
  "Customer_ID",
  "Customer_Name",
  "Region",
  "Country",
  "Product_Category",
  "Product_Name",
  "Sales_Rep",
  "Channel",
  "Quantity",
  "Units_Sold",
  "Unit_Price",
  "Revenue",
  "Gross_Sales",
  "Net_Sales",
  "Cost",
  "Profit",
  "Discount_%",
  "Target_Sales",
  "Payment_Status",
  "Order_Status",
];

const FINANCE_TEMPLATE_HEADERS = [
  "Transaction_ID",
  "Date",
  "Account_Type",
  "Category",
  "Sub_Category",
  "Department",
  "Region",
  "Cost_Center",
  "Revenue",
  "Expense",
  "Cost",
  "Profit",
  "Budget",
  "Actual",
  "Variance",
  "Payment_Status",
];


const MARKETING_TEMPLATE_HEADERS = [
  "Campaign_ID",
  "Campaign_Name",
  "Date",
  "Source",
  "Channel",
  "Region",
  "Impressions",
  "Clicks",
  "Leads",
  "Conversions",
  "Spend",
  "Revenue",
  "CTR",
  "Conversion_Rate",
  "Cost_Per_Lead",
  "ROI",
];


const DEMO_HR = [
  {
    Employee_ID: "E001",
    Job_ID: "J101",
    Job_Title: "AI Engineer",
    Department: "Engineering",
    Country: "India",
    Experience_Level: "Mid (3-5 yrs)",
    Years_Experience: 4,
    Employment_Type: "Full-time",
    Work_Mode: "Remote",
    Salary_USD: 32000,
    Salary: 32000,
    Hire_Date: "2024-03-05",
    Exit_Date: "",
    Status: "Active",
    Job_Posted_Date: "2025-01-10",
    Job_Status: "Open",
    Applicants_Count: 52,
  },
  {
    Employee_ID: "E002",
    Job_ID: "J102",
    Job_Title: "Data Analyst",
    Department: "Analytics",
    Country: "UAE",
    Experience_Level: "Senior (6-9 yrs)",
    Years_Experience: 7,
    Employment_Type: "Full-time",
    Work_Mode: "Hybrid",
    Salary_USD: 54000,
    Salary: 54000,
    Hire_Date: "2023-07-15",
    Exit_Date: "",
    Status: "Active",
    Job_Posted_Date: "2025-02-11",
    Job_Status: "Open",
    Applicants_Count: 39,
  },
  {
    Employee_ID: "E003",
    Job_ID: "J103",
    Job_Title: "HR Specialist",
    Department: "HR",
    Country: "India",
    Experience_Level: "Entry (0-2 yrs)",
    Years_Experience: 2,
    Employment_Type: "Full-time",
    Work_Mode: "On-site",
    Salary_USD: 18000,
    Salary: 18000,
    Hire_Date: "2025-01-12",
    Exit_Date: "",
    Status: "Active",
    Job_Posted_Date: "2025-01-25",
    Job_Status: "Closed",
    Applicants_Count: 21,
  },
  {
    Employee_ID: "E004",
    Job_ID: "J104",
    Job_Title: "AI Engineer",
    Department: "Engineering",
    Country: "Singapore",
    Experience_Level: "Lead (10+ yrs)",
    Years_Experience: 11,
    Employment_Type: "Contract",
    Work_Mode: "Remote",
    Salary_USD: 98000,
    Salary: 98000,
    Hire_Date: "2022-10-01",
    Exit_Date: "2025-02-01",
    Status: "Exited",
    Job_Posted_Date: "2025-02-20",
    Job_Status: "Open",
    Applicants_Count: 63,
  },
  {
    Employee_ID: "E005",
    Job_ID: "J105",
    Job_Title: "Talent Partner",
    Department: "HR",
    Country: "France",
    Experience_Level: "Mid (3-5 yrs)",
    Years_Experience: 5,
    Employment_Type: "Full-time",
    Work_Mode: "Hybrid",
    Salary_USD: 42000,
    Salary: 42000,
    Hire_Date: "2024-08-19",
    Exit_Date: "",
    Status: "Active",
    Job_Posted_Date: "2025-03-01",
    Job_Status: "Open",
    Applicants_Count: 28,
  },
  {
    Employee_ID: "E006",
    Job_ID: "J106",
    Job_Title: "ML Engineer",
    Department: "Engineering",
    Country: "Germany",
    Experience_Level: "Senior (6-9 yrs)",
    Years_Experience: 8,
    Employment_Type: "Full-time",
    Work_Mode: "Remote",
    Salary_USD: 76000,
    Salary: 76000,
    Hire_Date: "2023-04-11",
    Exit_Date: "",
    Status: "Active",
    Job_Posted_Date: "2025-03-18",
    Job_Status: "Open",
    Applicants_Count: 74,
  },
];

const DEMO_SALES = [
  {
    Order_ID: "O001",
    Order_Date: "2025-01-05",
    Customer_ID: "C001",
    Customer_Name: "Alpha Corp",
    Region: "West",
    Country: "India",
    Product_Category: "Software",
    Product_Name: "Analytics Pro",
    Sales_Rep: "Rahul",
    Channel: "Direct",
    Quantity: 8,
    Units_Sold: 8,
    Unit_Price: 2500,
    Revenue: 20000,
    Gross_Sales: 20000,
    Net_Sales: 20000,
    Cost: 12000,
    Profit: 8000,
    "Discount_%": 5,
    Target_Sales: 22000,
    Payment_Status: "Received",
    Order_Status: "Completed",
  },
  {
    Order_ID: "O002",
    Order_Date: "2025-01-14",
    Customer_ID: "C002",
    Customer_Name: "Nova Tech",
    Region: "North",
    Country: "UAE",
    Product_Category: "Consulting",
    Product_Name: "Strategy Pack",
    Sales_Rep: "Aisha",
    Channel: "Partner",
    Quantity: 2,
    Units_Sold: 2,
    Unit_Price: 10000,
    Revenue: 20000,
    Gross_Sales: 20000,
    Net_Sales: 20000,
    Cost: 9000,
    Profit: 11000,
    "Discount_%": 0,
    Target_Sales: 23000,
    Payment_Status: "Received",
    Order_Status: "Completed",
  },
  {
    Order_ID: "O003",
    Order_Date: "2025-02-02",
    Customer_ID: "C003",
    Customer_Name: "Sigma Retail",
    Region: "South",
    Country: "India",
    Product_Category: "Software",
    Product_Name: "BI Cloud",
    Sales_Rep: "Rahul",
    Channel: "Online",
    Quantity: 12,
    Units_Sold: 12,
    Unit_Price: 1800,
    Revenue: 21600,
    Gross_Sales: 21600,
    Net_Sales: 21600,
    Cost: 11000,
    Profit: 10600,
    "Discount_%": 10,
    Target_Sales: 24000,
    Payment_Status: "Received",
    Order_Status: "Completed",
  },
  {
    Order_ID: "O004",
    Order_Date: "2025-02-20",
    Customer_ID: "C004",
    Customer_Name: "Global Foods",
    Region: "East",
    Country: "Singapore",
    Product_Category: "Training",
    Product_Name: "Power BI Bootcamp",
    Sales_Rep: "Meera",
    Channel: "Direct",
    Quantity: 15,
    Units_Sold: 15,
    Unit_Price: 900,
    Revenue: 13500,
    Gross_Sales: 13500,
    Net_Sales: 13500,
    Cost: 7000,
    Profit: 6500,
    "Discount_%": 3,
    Target_Sales: 15000,
    Payment_Status: "Received",
    Order_Status: "Completed",
  },
  {
    Order_ID: "O005",
    Order_Date: "2025-03-07",
    Customer_ID: "C005",
    Customer_Name: "Zen Manufacturing",
    Region: "West",
    Country: "Germany",
    Product_Category: "Consulting",
    Product_Name: "Data Migration",
    Sales_Rep: "Aisha",
    Channel: "Direct",
    Quantity: 1,
    Units_Sold: 1,
    Unit_Price: 18000,
    Revenue: 18000,
    Gross_Sales: 18000,
    Net_Sales: 18000,
    Cost: 8000,
    Profit: 10000,
    "Discount_%": 0,
    Target_Sales: 21000,
    Payment_Status: "Received",
    Order_Status: "Completed",
  },
  {
    Order_ID: "O006",
    Order_Date: "2025-03-25",
    Customer_ID: "C006",
    Customer_Name: "Peak Labs",
    Region: "North",
    Country: "France",
    Product_Category: "Software",
    Product_Name: "Forecast Suite",
    Sales_Rep: "Meera",
    Channel: "Online",
    Quantity: 6,
    Units_Sold: 6,
    Unit_Price: 3200,
    Revenue: 19200,
    Gross_Sales: 19200,
    Net_Sales: 19200,
    Cost: 12000,
    Profit: 7200,
    "Discount_%": 8,
    Target_Sales: 21000,
    Payment_Status: "Pending",
    Order_Status: "Pending",
  },
];

const DEMO_FINANCE = [
  {
    Transaction_ID: "T001",
    Date: "2025-01-10",
    Account_Type: "Income",
    Category: "Subscription",
    Sub_Category: "Monthly",
    Department: "Analytics",
    Region: "India",
    Cost_Center: "Analytics",
    Revenue: 28000,
    Expense: 5000,
    Cost: 4000,
    Profit: 23000,
    Budget: 25000,
    Actual: 28000,
    Variance: 3000,
    Payment_Status: "Received",
  },
  {
    Transaction_ID: "T002",
    Date: "2025-01-18",
    Account_Type: "Expense",
    Category: "Marketing",
    Sub_Category: "Ads",
    Department: "Marketing",
    Region: "UAE",
    Cost_Center: "Marketing",
    Revenue: 0,
    Expense: 12000,
    Cost: 12000,
    Profit: -12000,
    Budget: 10000,
    Actual: 12000,
    Variance: 2000,
    Payment_Status: "Paid",
  },
  {
    Transaction_ID: "T003",
    Date: "2025-02-03",
    Account_Type: "Income",
    Category: "Services",
    Sub_Category: "Dashboard Setup",
    Department: "Delivery",
    Region: "India",
    Cost_Center: "Delivery",
    Revenue: 42000,
    Expense: 14000,
    Cost: 11000,
    Profit: 28000,
    Budget: 35000,
    Actual: 42000,
    Variance: 7000,
    Payment_Status: "Received",
  },
  {
    Transaction_ID: "T004",
    Date: "2025-02-22",
    Account_Type: "Expense",
    Category: "Operations",
    Sub_Category: "Cloud",
    Department: "Engineering",
    Region: "Singapore",
    Cost_Center: "Engineering",
    Revenue: 0,
    Expense: 9000,
    Cost: 9000,
    Profit: -9000,
    Budget: 8500,
    Actual: 9000,
    Variance: 500,
    Payment_Status: "Paid",
  },
  {
    Transaction_ID: "T005",
    Date: "2025-03-09",
    Account_Type: "Income",
    Category: "Consulting",
    Sub_Category: "Retention",
    Department: "Consulting",
    Region: "Germany",
    Cost_Center: "Consulting",
    Revenue: 36000,
    Expense: 10000,
    Cost: 9000,
    Profit: 26000,
    Budget: 30000,
    Actual: 36000,
    Variance: 6000,
    Payment_Status: "Received",
  },
  {
    Transaction_ID: "T006",
    Date: "2025-03-28",
    Account_Type: "Expense",
    Category: "People",
    Sub_Category: "Payroll",
    Department: "HR",
    Region: "India",
    Cost_Center: "HR",
    Revenue: 0,
    Expense: 18000,
    Cost: 18000,
    Profit: -18000,
    Budget: 17000,
    Actual: 18000,
    Variance: 1000,
    Payment_Status: "Pending",
  },
];


const DEMO_MARKETING = [
  {
    Campaign_ID: "M001",
    Campaign_Name: "Google Search Lead Gen",
    Date: "2025-01-08",
    Source: "Google",
    Channel: "Search",
    Region: "West",
    Impressions: 120000,
    Clicks: 5200,
    Leads: 780,
    Conversions: 138,
    Spend: 14500,
    Revenue: 62000,
    CTR: 4.33,
    Conversion_Rate: 17.69,
    Cost_Per_Lead: 18.59,
    ROI: 327.59,
  },
  {
    Campaign_ID: "M002",
    Campaign_Name: "Meta Awareness Push",
    Date: "2025-01-20",
    Source: "Meta",
    Channel: "Social",
    Region: "South",
    Impressions: 210000,
    Clicks: 6800,
    Leads: 560,
    Conversions: 74,
    Spend: 9800,
    Revenue: 32500,
    CTR: 3.24,
    Conversion_Rate: 13.21,
    Cost_Per_Lead: 17.5,
    ROI: 231.63,
  },
  {
    Campaign_ID: "M003",
    Campaign_Name: "LinkedIn B2B Demo Campaign",
    Date: "2025-02-05",
    Source: "LinkedIn",
    Channel: "Paid Social",
    Region: "North",
    Impressions: 48000,
    Clicks: 2100,
    Leads: 310,
    Conversions: 96,
    Spend: 12500,
    Revenue: 78000,
    CTR: 4.38,
    Conversion_Rate: 30.97,
    Cost_Per_Lead: 40.32,
    ROI: 524,
  },
  {
    Campaign_ID: "M004",
    Campaign_Name: "Email Re-activation",
    Date: "2025-02-18",
    Source: "Email",
    Channel: "Email",
    Region: "East",
    Impressions: 36000,
    Clicks: 2900,
    Leads: 430,
    Conversions: 88,
    Spend: 3200,
    Revenue: 36000,
    CTR: 8.06,
    Conversion_Rate: 20.47,
    Cost_Per_Lead: 7.44,
    ROI: 1025,
  },
  {
    Campaign_ID: "M005",
    Campaign_Name: "YouTube Product Explainer",
    Date: "2025-03-03",
    Source: "YouTube",
    Channel: "Video",
    Region: "West",
    Impressions: 180000,
    Clicks: 4100,
    Leads: 390,
    Conversions: 62,
    Spend: 7600,
    Revenue: 28000,
    CTR: 2.28,
    Conversion_Rate: 15.9,
    Cost_Per_Lead: 19.49,
    ROI: 268.42,
  },
  {
    Campaign_ID: "M006",
    Campaign_Name: "Partner Webinar Leads",
    Date: "2025-03-22",
    Source: "Partner",
    Channel: "Webinar",
    Region: "Central",
    Impressions: 15000,
    Clicks: 1600,
    Leads: 520,
    Conversions: 144,
    Spend: 5400,
    Revenue: 91000,
    CTR: 10.67,
    Conversion_Rate: 27.69,
    Cost_Per_Lead: 10.38,
    ROI: 1585.19,
  },
];

const buildHrTemplateRow = (row) => ({
  ...row,
  Salary: row.Salary || row.Salary_USD || 0,
  Salary_USD: row.Salary_USD || row.Salary || 0,
});

const buildSalesTemplateRow = (row) => {
  const revenue = Number(row.Revenue || row.Net_Sales || row.Gross_Sales || 0);
  const cost = Number(row.Cost || 0);
  const profit = Number(row.Profit || revenue - cost);

  return {
    ...row,
    Quantity: row.Quantity || row.Units_Sold || 0,
    Units_Sold: row.Units_Sold || row.Quantity || 0,
    Revenue: revenue,
    Gross_Sales: row.Gross_Sales || revenue,
    Net_Sales: row.Net_Sales || revenue,
    Profit: profit,
    Target_Sales: row.Target_Sales || Math.round(revenue * 1.1),
    Payment_Status:
      row.Payment_Status ||
      (String(row.Order_Status || "").toLowerCase() === "completed"
        ? "Received"
        : "Pending"),
  };
};

const buildFinanceTemplateRow = (row) => ({
  ...row,
  Cost_Center: row.Cost_Center || row.Department || "General",
  Profit:
    row.Profit !== undefined && row.Profit !== ""
      ? row.Profit
      : Number(row.Revenue || 0) - Number(row.Expense || 0),
  Variance:
    row.Variance !== undefined && row.Variance !== ""
      ? row.Variance
      : Number(row.Actual || 0) - Number(row.Budget || 0),
});



const buildMarketingTemplateRow = (row) => {
  const impressions = Number(row.Impressions || 0);
  const clicks = Number(row.Clicks || 0);
  const leads = Number(row.Leads || 0);
  const conversions = Number(row.Conversions || 0);
  const spend = Number(row.Spend || 0);
  const revenue = Number(row.Revenue || 0);

  return {
    ...row,
    CTR:
      row.CTR !== undefined && row.CTR !== ""
        ? Number(row.CTR)
        : impressions
        ? (clicks / impressions) * 100
        : 0,
    Conversion_Rate:
      row.Conversion_Rate !== undefined && row.Conversion_Rate !== ""
        ? Number(row.Conversion_Rate)
        : leads
        ? (conversions / leads) * 100
        : 0,
    Cost_Per_Lead:
      row.Cost_Per_Lead !== undefined && row.Cost_Per_Lead !== ""
        ? Number(row.Cost_Per_Lead)
        : leads
        ? spend / leads
        : 0,
    ROI:
      row.ROI !== undefined && row.ROI !== ""
        ? Number(row.ROI)
        : spend
        ? ((revenue - spend) / spend) * 100
        : 0,
  };
};


function ProtectedRoute({ children, role }) {
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  if (!token) {
    return <Navigate to="/" replace />;
  }

  if (role && user.role !== role) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function LoginPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", rememberMe: true });
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMsg, setForgotMsg] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setMsg("");

      const payload = {
        email: form.email.trim(),
        password: form.password,
        rememberMe: form.rememberMe,
      };

      const res = await API.post("/auth/login", payload);

      if (!res?.data?.token || !res?.data?.user) {
        throw new Error("Login response is incomplete");
      }

      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));

      setMsg("Login successful");

      if (res.data.user.role === "admin") {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setMsg(getErrorMessage(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    try {
      setForgotLoading(true);
      setForgotMsg("");
      const res = await API.post("/auth/forgot-password", {
        email: forgotEmail.trim(),
      });
      setForgotMsg(
        res?.data?.message ||
          "If this email exists, a reset link has been sent."
      );
    } catch (err) {
      setForgotMsg(getErrorMessage(err, "Failed to send reset link"));
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div style={authStyles.page}>
      <div style={authStyles.cardLarge}>
        <div style={authStyles.brandPill}>Bivora Smart Analytics</div>
        <h2 style={authStyles.title}>Welcome Back</h2>
        <p style={authStyles.sub}>
          Sign in to access your dashboards, demos, and premium analytics workspace.
        </p>

        <form onSubmit={handleLogin} style={authStyles.form}>
          <input
            style={authStyles.input}
            type="email"
            placeholder="Work Email"
            value={form.email}
            onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
            required
          />
          <input
            style={authStyles.input}
            type="password"
            placeholder="Password"
            value={form.password}
            onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
            required
          />

          <div style={authStyles.rowBetween}>
            <label style={authStyles.checkboxWrap}>
              <input
                type="checkbox"
                checked={form.rememberMe}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, rememberMe: e.target.checked }))
                }
              />
              <span>Remember me</span>
            </label>

            <button
              type="button"
              onClick={() => setShowForgot((prev) => !prev)}
              style={authStyles.textButton}
            >
              Forgot Password?
            </button>
          </div>

          <button style={authStyles.button} type="submit" disabled={loading}>
            {loading ? "Logging in..." : "Login to Dashboard"}
          </button>
        </form>

        {showForgot ? (
          <div style={authStyles.infoBox}>
            <h3 style={authStyles.infoTitle}>Reset Password</h3>
            <p style={authStyles.infoSub}>
              Enter your email and we’ll send a reset link.
            </p>
            <form onSubmit={handleForgotPassword} style={authStyles.form}>
              <input
                style={authStyles.input}
                type="email"
                placeholder="Enter your email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
              />
              <button style={authStyles.secondaryButton} type="submit" disabled={forgotLoading}>
                {forgotLoading ? "Sending..." : "Send Reset Link"}
              </button>
            </form>
            {forgotMsg ? <p style={authStyles.msg}>{forgotMsg}</p> : null}
          </div>
        ) : null}

        {msg ? <p style={authStyles.msg}>{msg}</p> : null}

        <p style={authStyles.linkText}>
          Don’t have an account? <Link to="/signup">Create Client Account</Link>
        </p>
      </div>
    </div>
  );
}

function SignupPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    companyName: "",
    mobileNumber: "",
    email: "",
    password: "",
  });
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSignup = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setMsg("");

      const payload = {
        name: form.name.trim(),
        companyName: form.companyName.trim(),
        mobileNumber: form.mobileNumber.trim(),
        email: form.email.trim(),
        password: form.password,
        role: "client",
      };

      const res = await API.post("/auth/signup", payload);
      setMsg(res?.data?.message || "Signup successful");

      setTimeout(() => navigate("/"), 1200);
    } catch (err) {
      setMsg(getErrorMessage(err, "Signup failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={authStyles.page}>
      <div style={authStyles.cardLarge}>
        <div style={authStyles.brandPill}>Client Registration</div>
        <h2 style={authStyles.title}>Create Your Account</h2>
        <p style={authStyles.sub}>
          Start with demo dashboards, one free upload trial, and upgrade whenever you’re ready.
        </p>

        <form onSubmit={handleSignup} style={authStyles.form}>
          <input
            style={authStyles.input}
            type="text"
            placeholder="Full Name"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            required
          />
          <input
            style={authStyles.input}
            type="text"
            placeholder="Company Name"
            value={form.companyName}
            onChange={(e) => setForm((prev) => ({ ...prev, companyName: e.target.value }))}
          />
          <input
            style={authStyles.input}
            type="text"
            placeholder="Mobile Number"
            value={form.mobileNumber}
            onChange={(e) => setForm((prev) => ({ ...prev, mobileNumber: e.target.value }))}
          />
          <input
            style={authStyles.input}
            type="email"
            placeholder="Work Email"
            value={form.email}
            onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
            required
          />
          <input
            style={authStyles.input}
            type="password"
            placeholder="Create Password"
            value={form.password}
            onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
            required
          />

          <div style={authStyles.infoBox}>
            <div style={authStyles.infoTitle}>Account Type</div>
            <div style={authStyles.infoSub}>
              All public signups are created as <strong>Client</strong>. Admin access is reserved for the internal team only.
            </div>
          </div>

          <button style={authStyles.button} type="submit" disabled={loading}>
            {loading ? "Creating Account..." : "Create Client Account"}
          </button>
        </form>

        {msg ? <p style={authStyles.msg}>{msg}</p> : null}

        <p style={authStyles.linkText}>
          Already have an account? <Link to="/">Login</Link>
        </p>
      </div>
    </div>
  );
}

function TopNav({ title }) {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const navigate = useNavigate();

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/");
  };

  return (
    <div style={navStyles.wrap}>
      <div>
        <div style={navStyles.title}>{title}</div>
        <div style={navStyles.sub}>
          Welcome {user?.name || "User"} • Role: {user?.role || "-"}
        </div>
      </div>

      <div style={navStyles.links}>
        {user?.role === "admin" ? <Link style={navStyles.link} to="/admin">Admin</Link> : null}
        {user?.role === "admin" ? <Link style={navStyles.link} to="/admin/users">User Access</Link> : null}
        {user?.role === "admin" ? <Link style={navStyles.link} to="/admin/leads">Enterprise Leads</Link> : null}
        <Link style={navStyles.link} to="/dashboard">Dashboard</Link>
        <button style={navStyles.button} onClick={logout}>Logout</button>
      </div>
    </div>
  );
}

function ClientDashboardPage() {
  return (
    <>
      <TopNav title="Client Dashboard" />
      <AnalyticsDashboard />
    </>
  );
}

function AdminDashboardPage() {
  return (
    <>
      <TopNav title="Admin Dashboard" />
      <AnalyticsDashboard />
    </>
  );
}

function AnalyticsDashboard() {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [sheetName, setSheetName] = useState("");
  const [dashboardType, setDashboardType] = useState("");
  const [dashboardName, setDashboardName] = useState("");
  const [activeTab, setActiveTab] = useState("story");
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({});
  const [theme, setTheme] = useState("dark");
  const [savedReports, setSavedReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState({
    hasActiveSubscription: false,
    subscription: null,
    isAdminBypass: false,
    freeUploadsRemaining: 0,
  });
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [paymentSuccess, setPaymentSuccess] = useState(null);
  const [shareLoadingId, setShareLoadingId] = useState(null);
  const [shareExpiry, setShareExpiry] = useState("7d");
  const [presentationMode, setPresentationMode] = useState(false);
  const [aiChatMessages, setAiChatMessages] = useState([]);
  const [aiQuestion, setAiQuestion] = useState("");
  const [drillDownEnabled, setDrillDownEnabled] = useState(true);
  const [builderKpis, setBuilderKpis] = useState(["revenue", "profit", "margin", "topPerformer"]);
  const [builderCharts, setBuilderCharts] = useState(["main", "trend"]);
  const [builderWidgets, setBuilderWidgets] = useState([
    { id: "builder-revenue", type: "revenue", title: "Revenue KPI" },
    { id: "builder-profit", type: "profit", title: "Profit KPI" },
    { id: "builder-chart", type: "chart", title: "Performance Chart" },
  ]);
  const [draggingBuilderId, setDraggingBuilderId] = useState(null);
  const [builderSaving, setBuilderSaving] = useState(false);
  const [compareFileAName, setCompareFileAName] = useState("");
  const [compareFileBName, setCompareFileBName] = useState("");
  const [compareRowsA, setCompareRowsA] = useState([]);
  const [compareRowsB, setCompareRowsB] = useState([]);
  const [apiUrl, setApiUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [apiAuthType, setApiAuthType] = useState("bearer");
  const [sqlFile, setSqlFile] = useState(null);
  const [apiLoading, setApiLoading] = useState(false);
  const [connectedRows, setConnectedRows] = useState([]);
  const [connectedRawRows, setConnectedRawRows] = useState([]);
  const [connectedFieldOptions, setConnectedFieldOptions] = useState([]);
  const [connectedFieldMapping, setConnectedFieldMapping] = useState({
    Revenue: "",
    Profit: "",
    Cost: "",
    Region: "",
    Department: "",
    Category: "",
    Date: "",
  });
  const [connectedDashboardType, setConnectedDashboardType] = useState("");
  const [connectedDashboardName, setConnectedDashboardName] = useState("");
  const [connectedSourceName, setConnectedSourceName] = useState("");
  const [builderDashboardType, setBuilderDashboardType] = useState("current");
  const [builderGeneratedInsight, setBuilderGeneratedInsight] = useState("");

  const exportRef = useRef(null);

  const currentTheme = theme === "dark" ? darkTheme : lightTheme;
  const s = styles(currentTheme);

  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const reportCacheKey = `bivora_saved_reports_${currentUser?.id || currentUser?.email || "guest"}`;

  const readCachedReports = () => {
    try {
      const cached = JSON.parse(localStorage.getItem(reportCacheKey) || "[]");
      return Array.isArray(cached) ? cached : [];
    } catch (error) {
      return [];
    }
  };

  const writeCachedReports = (reports) => {
    try {
      localStorage.setItem(reportCacheKey, JSON.stringify(Array.isArray(reports) ? reports : []));
    } catch (error) {
      console.warn("Could not cache reports locally:", error?.message || error);
    }
  };

  const mergeReports = (freshReports, cachedReports = []) => {
    const map = new Map();
    [...freshReports, ...cachedReports].forEach((report) => {
      if (!report) return;
      map.set(String(report.id), report);
    });
    return Array.from(map.values()).sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  };

  const formatCurrency = (num) => {
    const value = Number(num || 0);
    return `$${value.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  };

  const formatNumber = (num) =>
    Number(num || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

  const formatPercent = (num) =>
    `${Number(num || 0).toLocaleString("en-US", { maximumFractionDigits: 1 })}%`;

  const detectTypeFromRows = (dataRows = []) => {
    const first = dataRows[0] || {};
    const keys = Object.keys(first);

    const hrScore = HR_TEMPLATE_HEADERS.filter((k) => keys.includes(k)).length;
    const salesScore = SALES_TEMPLATE_HEADERS.filter((k) => keys.includes(k)).length;
    const financeScore = FINANCE_TEMPLATE_HEADERS.filter((k) => keys.includes(k)).length;
    const marketingScore = MARKETING_TEMPLATE_HEADERS.filter((k) => keys.includes(k)).length;

    const scores = [
      { dashboardType: "hr", dashboardName: "HR Analytics Dashboard", score: hrScore },
      { dashboardType: "sales", dashboardName: "Sales Analytics Dashboard", score: salesScore },
      { dashboardType: "finance", dashboardName: "Finance Analytics Dashboard", score: financeScore },
      { dashboardType: "marketing", dashboardName: "Marketing Analytics Dashboard", score: marketingScore },
    ].sort((a, b) => b.score - a.score);

    return { dashboardType: scores[0].dashboardType, dashboardName: scores[0].dashboardName };
  };

  const fetchSubscription = async () => {
    try {
      setSubscriptionLoading(true);
      const res = await API.get("/subscription/me");
      setSubscriptionInfo({
        hasActiveSubscription: res?.data?.hasActiveSubscription || false,
        subscription: res?.data?.subscription || null,
        isAdminBypass: res?.data?.isAdminBypass || false,
        freeUploadsRemaining: res?.data?.freeUploadsRemaining || 0,
      });
    } catch (error) {
      console.error("Fetch subscription error:", error);
      setSubscriptionInfo({
        hasActiveSubscription: false,
        subscription: null,
        isAdminBypass: false,
        freeUploadsRemaining: 0,
      });
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const fetchMyReports = async () => {
    const cachedReports = readCachedReports();

    try {
      setReportsLoading(true);

      if (cachedReports.length > 0) {
        setSavedReports(cachedReports);
      }

      const res = await API.get("/reports/my-reports");
      const reportsFromDatabase = Array.isArray(res?.data?.reports) ? res.data.reports : [];

      const finalReports =
        reportsFromDatabase.length > 0
          ? mergeReports(reportsFromDatabase, cachedReports)
          : cachedReports;

      setSavedReports(finalReports);
      writeCachedReports(finalReports);

      return finalReports;
    } catch (error) {
      console.error("Fetch reports error:", error);

      if (cachedReports.length > 0) {
        setSavedReports(cachedReports);
        return cachedReports;
      }

      setMessage(error?.response?.data?.message || "Could not refresh My Reports. Please check backend.");
      return [];
    } finally {
      setReportsLoading(false);
    }
  };

  const saveCurrentReport = async () => {
    try {
      if (!dashboardType || !rows.length) {
        setMessage("Please upload or load a report first");
        return;
      }

      const payload = {
        reportName: dashboardName || `${dashboardType.toUpperCase()} Report`,
        dashboardType,
        sheetName,
        totalRows: rows.length,
        rows,
      };

      const res = await API.post("/reports/save", payload);

      if (res.data.success) {
        setMessage("Report saved successfully");

        if (res?.data?.report) {
          const savedReportFromApi = res.data.report;

          setSavedReports((prev) => {
            const merged = mergeReports([savedReportFromApi], prev);
            writeCachedReports(merged);
            return merged;
          });
        }

        await fetchMyReports();
      }
    } catch (error) {
      console.error("Save report error:", error);
      setMessage(error?.response?.data?.message || "Failed to save report");
    }
  };

  const loadSavedReport = async (reportId) => {
    try {
      let report = null;

      if (String(reportId).startsWith("temp-")) {
        report = savedReports.find((item) => String(item.id) === String(reportId));
      } else {
        const res = await API.get(`/reports/${reportId}`);
        if (res.data.success) {
          report = res.data.report;
        }
      }

      if (report) {
        setRows(report.reportData || []);
        setDashboardType(report.dashboardType);
        setDashboardName(report.reportName);
        setSheetName(report.sheetName || "");
        setFilters({});
        setSearchText("");
        setActiveTab("story");
        setMessage("Saved report loaded successfully");
      }
    } catch (error) {
      console.error("Load report error:", error);
      setMessage(error?.response?.data?.message || "Failed to load report");
    }
  };

  const deleteSavedReport = async (reportId) => {
    try {
      if (String(reportId).startsWith("temp-")) {
        setSavedReports((prev) => {
          const updated = prev.filter((item) => String(item.id) !== String(reportId));
          writeCachedReports(updated);
          return updated;
        });
        setMessage("Report removed from current session");
        return;
      }

      const res = await API.delete(`/reports/${reportId}`);

      if (res.data.success) {
        setMessage("Report deleted successfully");
        setSavedReports((prev) => {
          const updated = prev.filter((item) => String(item.id) !== String(reportId));
          writeCachedReports(updated);
          return updated;
        });
        await fetchMyReports();
      }
    } catch (error) {
      console.error("Delete report error:", error);
      setMessage(error?.response?.data?.message || "Failed to delete report");
    }
  };
  const generateShareLink = async (reportId) => {
    try {
      if (!hasPremiumAccess) {
        setMessage("Sharing is available only for paid users, admins, and internal team members.");
        return;
      }

      setShareLoadingId(reportId);

      const res = await API.post(`/reports/${reportId}/share`, { expiry: shareExpiry });

      if (res.data.success) {
        const shareUrl = res.data.shareUrl;
        const updatedShare = {
          token: res.data.token,
          shareUrl,
          expiresAt: res.data.expiresAt,
          viewCount: res.data.viewCount || 0,
          lastAccessedAt: res.data.lastAccessedAt || null,
          isActive: true,
        };

        setSavedReports((prev) =>
          prev.map((report) =>
            String(report.id) === String(reportId)
              ? { ...report, share: updatedShare }
              : report
          )
        );

        if (shareUrl) {
          try {
            if (navigator.clipboard && window.isSecureContext) {
              await navigator.clipboard.writeText(shareUrl);
              setMessage("Share link generated and copied successfully. It will stay visible under My Reports.");
            } else {
              window.prompt("Copy this share link:", shareUrl);
              setMessage("Share link generated. Copy it from the popup box.");
            }
          } catch (copyError) {
            window.prompt("Copy this share link:", shareUrl);
            setMessage("Share link generated. Copy it from the popup box.");
          }
        }
      }
    } catch (error) {
      console.error("Generate share link error:", error);
      setMessage(error?.response?.data?.message || "Failed to generate share link");
    } finally {
      setShareLoadingId(null);
    }
  };

  const revokeShareLink = async (reportId) => {
    try {
      setShareLoadingId(reportId);
      const res = await API.post(`/reports/${reportId}/share/revoke`);
      if (res.data.success) {
        setSavedReports((prev) =>
          prev.map((report) =>
            String(report.id) === String(reportId)
              ? { ...report, share: null }
              : report
          )
        );
        setMessage("Share link disabled successfully.");
      }
    } catch (error) {
      console.error("Revoke share link error:", error);
      setMessage(error?.response?.data?.message || "Failed to revoke share link");
    } finally {
      setShareLoadingId(null);
    }
  };

  const moveBuilderWidget = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= builderWidgets.length) return;
    setBuilderWidgets((prev) => {
      const next = [...prev];
      const [item] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, item);
      return next;
    });
  };

  const addBuilderWidget = (type) => {
    const labels = {
      revenue: "Revenue KPI",
      profit: "Profit KPI",
      margin: "Margin KPI",
      chart: "Bar Chart Widget",
      line: "Trend Line Chart",
      pie: "Segment Pie Chart",
      insight: "Insight Card",
    };
    setBuilderWidgets((prev) => [
      ...prev,
      { id: `builder-${type}-${Date.now()}`, type, title: labels[type] || "Custom Widget" },
    ]);
  };

  const removeBuilderWidget = (id) => {
    setBuilderWidgets((prev) => prev.filter((item) => item.id !== id));
  };

  const saveBuilderLayout = async () => {
    try {
      setBuilderSaving(true);
      await API.post("/builder/save", { layout: builderWidgets, dashboardType: builderDashboardType, generatedInsight: builderGeneratedInsight });
      setMessage("Custom dashboard builder layout saved successfully.");
    } catch (error) {
      console.error("Save builder layout error:", error);
      setMessage(error?.response?.data?.message || "Failed to save builder layout");
    } finally {
      setBuilderSaving(false);
    }
  };


  useEffect(() => {
    const cachedReports = readCachedReports();
    if (cachedReports.length > 0) {
      setSavedReports(cachedReports);
    }

    fetchMyReports();
    fetchSubscription();
  }, []);

  useEffect(() => {
    const loadBuilderLayout = async () => {
      try {
        const res = await API.get("/builder/load");
        const layout = res?.data?.layout;
        if (Array.isArray(layout) && layout.length > 0) {
          setBuilderWidgets(layout);
        }
        if (res?.data?.dashboardType) setBuilderDashboardType(res.data.dashboardType);
        if (res?.data?.generatedInsight) setBuilderGeneratedInsight(res.data.generatedInsight);
      } catch (error) {
        console.warn("Builder layout load skipped:", error?.message || error);
      }
    };
    loadBuilderLayout();
  }, []);

  const hasPaidAccess = subscriptionInfo?.hasActiveSubscription;
  const isAdminBypass = subscriptionInfo?.isAdminBypass || false;
  const freeUploadsRemaining = subscriptionInfo?.freeUploadsRemaining || 0;
  const hasUploadAccess = hasPaidAccess || isAdminBypass || freeUploadsRemaining > 0;
  const hasPremiumAccess = hasPaidAccess || isAdminBypass;

  const uploadFile = async () => {
    if (!hasUploadAccess) {
      setMessage("Your 1 free upload trial is finished. Please subscribe to continue.");
      return;
    }

    if (!file) {
      setMessage("Please select a file first");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setLoading(true);
      setMessage("Uploading file and preparing dashboard...");
      setRows([]);
      setDashboardType("");
      setDashboardName("");
      setSheetName("");
      setFilters({});
      setSearchText("");
      setActiveTab("story");

      const token = localStorage.getItem("token");

      const res = await axios.post("https://api.bivoratech.com/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: token ? `Bearer ${token}` : "",
        },
        timeout: 30000,
      });

      if (res.data.success) {
        const uploadedRows = res.data.rows || [];
        const fallback = detectTypeFromRows(uploadedRows);

        setRows(uploadedRows);
        setSheetName(res.data.sheetName || "");
        setDashboardType(res.data.dashboardType || fallback.dashboardType);
        setDashboardName(res.data.dashboardName || fallback.dashboardName);

        if (res.data.usedFreeTrial) {
          setMessage(
            `${res.data.dashboardName || fallback.dashboardName} loaded successfully. Your free upload trial has now been used.`
          );
        } else {
          setMessage(`${res.data.dashboardName || fallback.dashboardName} loaded successfully.`);
        }

        fetchSubscription();
      } else {
        setMessage(res.data.message || "Upload failed");
      }
    } catch (error) {
      console.error(error);
      setMessage(error?.response?.data?.message || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const loadDemo = (type) => {
    if (type === "hr") {
      setRows(DEMO_HR.map(buildHrTemplateRow));
      setDashboardType("hr");
      setDashboardName("HR Analytics Dashboard");
      setSheetName("Demo_HR");
    }

    if (type === "sales") {
      setRows(DEMO_SALES.map(buildSalesTemplateRow));
      setDashboardType("sales");
      setDashboardName("Sales Analytics Dashboard");
      setSheetName("Demo_Sales");
    }

    if (type === "finance") {
      setRows(DEMO_FINANCE.map(buildFinanceTemplateRow));
      setDashboardType("finance");
      setDashboardName("Finance Analytics Dashboard");
      setSheetName("Demo_Finance");
    }
    if (type === "marketing") {
      setRows(DEMO_MARKETING.map(buildMarketingTemplateRow));
      setDashboardType("marketing");
      setDashboardName("Marketing Analytics Dashboard");
      setSheetName("Demo_Marketing");
    }

setFilters({});
    setSearchText("");
    setActiveTab("story");
    setMessage(`${type.toUpperCase()} demo dashboard loaded.`);
  };

  const downloadCSV = (filename, headers, dataRows) => {
    const csvRows = [
      headers.join(","),
      ...dataRows.map((row) =>
        headers.map((header) => `"${String(row[header] ?? "").replace(/"/g, '""')}"`).join(",")
      ),
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const downloadTemplate = (type) => {
    if (type === "hr") {
      downloadCSV("bivora_hr_template.csv", HR_TEMPLATE_HEADERS, [buildHrTemplateRow(DEMO_HR[0])]);
    }
    if (type === "sales") {
      downloadCSV("bivora_sales_template.csv", SALES_TEMPLATE_HEADERS, [buildSalesTemplateRow(DEMO_SALES[0])]);
    }
    if (type === "finance") {
      downloadCSV("bivora_finance_template.csv", FINANCE_TEMPLATE_HEADERS, [buildFinanceTemplateRow(DEMO_FINANCE[0])]);
    }
    if (type === "marketing") {
      downloadCSV("bivora_marketing_template.csv", MARKETING_TEMPLATE_HEADERS, [buildMarketingTemplateRow(DEMO_MARKETING[0])]);
    }
};

  const optionSet = useMemo(() => {
    const result = {};

    const addUnique = (key, value) => {
      if (!value) return;
      if (!result[key]) result[key] = new Set();
      result[key].add(value);
    };

    rows.forEach((row) => {
      if (dashboardType === "hr") {
        addUnique("Country", row.Country);
        addUnique("Department", row.Department);
        addUnique("Status", row.Status);
        addUnique("Work_Mode", row.Work_Mode);
        addUnique("Experience_Level", row.Experience_Level);
      }

      if (dashboardType === "sales") {
        addUnique("Region", row.Region);
        addUnique("Country", row.Country);
        addUnique("Channel", row.Channel);
        addUnique("Order_Status", row.Order_Status);
        addUnique("Payment_Status", row.Payment_Status);
        addUnique("Product_Category", row.Product_Category);
      }

      if (dashboardType === "finance") {
        addUnique("Department", row.Department);
        addUnique("Region", row.Region);
        addUnique("Category", row.Category);
        addUnique("Payment_Status", row.Payment_Status);
        addUnique("Account_Type", row.Account_Type);
        addUnique("Cost_Center", row.Cost_Center);
      }
      // Marketing filter options
      if (dashboardType === "marketing") {
        addUnique("Source", row.Source);
        addUnique("Channel", row.Channel);
        addUnique("Region", row.Region);
        addUnique("Campaign_Name", row.Campaign_Name);
      }
});

    return Object.fromEntries(
      Object.entries(result).map(([key, valueSet]) => [key, [...valueSet].sort()])
    );
  }, [rows, dashboardType]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const searchOk =
        !searchText ||
        Object.values(row).some((value) =>
          String(value ?? "").toLowerCase().includes(searchText.toLowerCase())
        );

      const filterOk = Object.entries(filters).every(([key, value]) => {
        if (!value) return true;
        return String(row[key] ?? "") === String(value);
      });

      return searchOk && filterOk;
    });
  }, [rows, filters, searchText]);

  const groupByCount = (dataRows, key) => {
    const map = {};
    dataRows.forEach((row) => {
      const label = row[key] || "Unknown";
      map[label] = (map[label] || 0) + 1;
    });
    return map;
  };

  const groupBySum = (dataRows, groupKey, valueKey) => {
    const map = {};
    dataRows.forEach((row) => {
      const label = row[groupKey] || "Unknown";
      map[label] = (map[label] || 0) + Number(row[valueKey] || 0);
    });
    return map;
  };

  const monthlySum = (dataRows, dateKey, valueKey) => {
    const map = {};
    dataRows.forEach((row) => {
      const rawDate = row[dateKey];
      const date = rawDate ? new Date(rawDate) : null;
      if (!date || Number.isNaN(date.getTime())) return;
      const label = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      map[label] = (map[label] || 0) + Number(row[valueKey] || 0);
    });
    return map;
  };

  const topEntries = (obj, limit = 8) =>
    Object.entries(obj || {})
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit);

  const commonChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      devicePixelRatio: Math.min(window.devicePixelRatio || 1, 2),
      resizeDelay: 150,
      interaction: { mode: "index", intersect: false },
      layout: { padding: { top: 12, right: 16, bottom: 6, left: 8 } },
      onClick: (event, elements, chart) => {
        if (!drillDownEnabled || !elements?.length || !chart?.data?.labels) return;
        const label = chart.data.labels[elements[0].index];
        if (!label) return;
        const possibleKeys = ["Region", "Department", "Country", "Source", "Channel", "Product_Category", "Product_Name", "Sales_Rep", "Campaign_Name", "Category", "Payment_Status", "Work_Mode"];
        const key = possibleKeys.find((item) => optionSet[item]?.includes(String(label)));
        if (key) {
          setFilters((prev) => ({ ...prev, [key]: String(label) }));
          setMessage(`Drill-down applied: ${key} = ${label}`);
        }
      },
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            color: currentTheme.text,
            font: { size: 12, weight: "700" },
            boxWidth: 10,
            boxHeight: 10,
            usePointStyle: true,
            padding: 18,
          },
        },
        datalabels: {
          display: false,
          color: currentTheme.chartLabel,
          backgroundColor: currentTheme.dataLabelBg,
          borderRadius: 6,
          padding: 4,
          font: { size: 10, weight: "700" },
          formatter: (value) => {
            if (typeof value === "number" && Math.abs(value) >= 1000) {
              return `${Math.round(value / 1000)}K`;
            }
            return value;
          },
          display: false,
        },
      },
      scales: {
        x: {
          border: { display: false },
          ticks: {
            color: currentTheme.muted,
            font: { size: 11, weight: "600" },
            maxRotation: 30,
            minRotation: 0,
            autoSkip: true,
            maxTicksLimit: 8,
          },
          grid: { color: currentTheme.grid, drawBorder: false },
        },
        y: {
          border: { display: false },
          beginAtZero: true,
          ticks: {
            color: currentTheme.muted,
            font: { size: 11, weight: "600" },
          },
          grid: { color: currentTheme.grid, drawBorder: false },
        },
      },
    }),
    [currentTheme, optionSet, drillDownEnabled]
  );

  const pieOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            color: currentTheme.text,
            font: { size: 12, weight: "700" },
            boxWidth: 10,
            boxHeight: 10,
            usePointStyle: true,
            padding: 18,
          },
        },
        datalabels: {
          display: false,
          color: currentTheme.chartLabel,
          backgroundColor: currentTheme.dataLabelBg,
          borderRadius: 6,
          padding: 4,
          font: { size: 10, weight: "700" },
          formatter: (value) => {
            if (typeof value === "number" && Math.abs(value) >= 1000) {
              return `${Math.round(value / 1000)}K`;
            }
            return value;
          },
          display: false,
        },
      },
    }),
    [currentTheme]
  );

  const lineOptions = useMemo(
    () => ({
      ...commonChartOptions,
      plugins: {
        ...commonChartOptions.plugins,
        datalabels: {
          display: false,
        },
      },
    }),
    [commonChartOptions]
  );

  const averageMap = (dataRows, groupKey, valueKey) => {
    const sumMap = {};
    const countMap = {};
    dataRows.forEach((row) => {
      const label = row[groupKey] || "Unknown";
      sumMap[label] = (sumMap[label] || 0) + Number(row[valueKey] || 0);
      countMap[label] = (countMap[label] || 0) + 1;
    });
    const result = {};
    Object.keys(sumMap).forEach((key) => {
      result[key] = countMap[key] ? sumMap[key] / countMap[key] : 0;
    });
    return result;
  };

  const ratioByGroup = (dataRows, groupKey, numeratorKey, denominatorKey, multiplier = 100) => {
    const numMap = {};
    const denMap = {};
    dataRows.forEach((row) => {
      const label = row[groupKey] || "Unknown";
      numMap[label] = (numMap[label] || 0) + Number(row[numeratorKey] || 0);
      denMap[label] = (denMap[label] || 0) + Number(row[denominatorKey] || 0);
    });
    const result = {};
    Object.keys(numMap).forEach((key) => {
      result[key] = denMap[key] ? (numMap[key] / denMap[key]) * multiplier : 0;
    });
    return result;
  };

  const hrData = useMemo(() => {
    if (dashboardType !== "hr") return null;

    const totalEmployees = filteredRows.length;
    const exitedEmployees = filteredRows.filter((row) => String(row.Exit_Date || row.Status || "").trim() && (String(row.Exit_Date || "").trim() || String(row.Status || "").toLowerCase() === "exited")).length;
    const activeEmployees = Math.max(totalEmployees - exitedEmployees, 0);
    const attritionRate = totalEmployees ? (exitedEmployees / totalEmployees) * 100 : 0;
    const avgSalary = totalEmployees
      ? filteredRows.reduce((sum, row) => sum + Number(row.Salary_USD || row.Salary || 0), 0) / totalEmployees
      : 0;
    const totalApplicants = filteredRows.reduce((sum, row) => sum + Number(row.Applicants_Count || 0), 0);
    const hiringConversionRate = totalApplicants ? (totalEmployees / totalApplicants) * 100 : 0;
    const openPositions = filteredRows.filter((row) => String(row.Job_Status || "").toLowerCase() === "open").length;
    const remoteCount = filteredRows.filter((row) => String(row.Work_Mode || "").toLowerCase() === "remote").length;
    const remotePct = totalEmployees ? (remoteCount / totalEmployees) * 100 : 0;

    const salaryByExperienceMap = averageMap(filteredRows, "Experience_Level", "Salary_USD");
    Object.keys(salaryByExperienceMap).forEach((key) => {
      if (!salaryByExperienceMap[key]) salaryByExperienceMap[key] = averageMap(filteredRows, "Experience_Level", "Salary")[key] || 0;
    });

    const demandRoleMap = groupBySum(filteredRows, "Job_Title", "Applicants_Count");
    const employeesByCountryMap = groupByCount(filteredRows, "Country");
    const employeeDistributionMap = groupByCount(filteredRows, "Department");
    const departmentMap = employeeDistributionMap;
    const workModeMap = groupByCount(filteredRows, "Work_Mode");
    const applicantsPerWorkModeMap = groupBySum(filteredRows, "Work_Mode", "Applicants_Count");
    const salaryTrendMap = monthlySum(filteredRows, "Hire_Date", "Salary_USD");

    const deptTotal = {};
    const deptExited = {};
    const deptApplicants = {};
    filteredRows.forEach((row) => {
      const dept = row.Department || "Unknown";
      deptTotal[dept] = (deptTotal[dept] || 0) + 1;
      deptApplicants[dept] = (deptApplicants[dept] || 0) + Number(row.Applicants_Count || 0);
      if (String(row.Exit_Date || "").trim() || String(row.Status || "").toLowerCase() === "exited") {
        deptExited[dept] = (deptExited[dept] || 0) + 1;
      }
    });
    const attritionByDepartmentMap = {};
    const hiringConversionByDepartmentMap = {};
    Object.keys(deptTotal).forEach((dept) => {
      attritionByDepartmentMap[dept] = deptTotal[dept] ? ((deptExited[dept] || 0) / deptTotal[dept]) * 100 : 0;
      hiringConversionByDepartmentMap[dept] = deptApplicants[dept] ? (deptTotal[dept] / deptApplicants[dept]) * 100 : 0;
    });

    const highestAttritionDepartment = topEntries(attritionByDepartmentMap, 1)[0]?.[0] || "-";
    const mostDemandRole = topEntries(demandRoleMap, 1)[0]?.[0] || "-";
    const topCountry = topEntries(employeesByCountryMap, 1)[0]?.[0] || "-";
    const topWorkModeByApplicants = topEntries(applicantsPerWorkModeMap, 1)[0]?.[0] || "-";
    const largestDepartment = topEntries(employeeDistributionMap, 1)[0]?.[0] || "-";

    return {
      totalEmployees,
      activeEmployees,
      exitedEmployees,
      attritionRate,
      highestAttritionDepartment,
      hiringConversionRate,
      avgSalary,
      openPositions,
      remotePct,
      salaryByExperienceMap,
      avgSalaryByExperienceMap: salaryByExperienceMap,
      demandRoleMap,
      employeesByCountryMap,
      employeeDistributionMap,
      departmentMap,
      workModeMap,
      applicantsPerWorkModeMap,
      salaryTrendMap,
      attritionByDepartmentMap,
      hiringConversionByDepartmentMap,
      mostDemandRole,
      topCountry,
      topWorkModeByApplicants,
      largestDepartment,
    };
  }, [dashboardType, filteredRows]);

  const salesData = useMemo(() => {
    if (dashboardType !== "sales") return null;

    const totalRevenue = filteredRows.reduce((sum, row) => sum + Number(row.Revenue || row.Net_Sales || row.Gross_Sales || 0), 0);
    const totalProfit = filteredRows.reduce((sum, row) => sum + Number(row.Profit || 0), 0);
    const totalOrders = filteredRows.length;
    const profitMargin = totalRevenue ? (totalProfit / totalRevenue) * 100 : 0;
    const avgOrderValue = totalOrders ? totalRevenue / totalOrders : 0;
    const profitPerOrder = totalOrders ? totalProfit / totalOrders : 0;
    const discountImpact = filteredRows.reduce((sum, row) => {
      const revenue = Number(row.Revenue || row.Net_Sales || row.Gross_Sales || 0);
      const discountPct = Number(row["Discount_%"] || 0);
      return sum + (revenue * discountPct) / 100;
    }, 0);
    const avgDiscount = totalOrders ? filteredRows.reduce((sum, row) => sum + Number(row["Discount_%"] || 0), 0) / totalOrders : 0;

    const topProductMap = groupBySum(filteredRows, "Product_Name", "Revenue");
    const productProfitMap = groupBySum(filteredRows, "Product_Name", "Profit");
    const quantityByProductMap = groupBySum(filteredRows, "Product_Name", "Quantity");
    const topRegionMap = groupBySum(filteredRows, "Region", "Revenue");
    const salesByChannelMap = groupBySum(filteredRows, "Channel", "Revenue");
    const revenueByRepMap = groupBySum(filteredRows, "Sales_Rep", "Revenue");
    const profitByRepMap = groupBySum(filteredRows, "Sales_Rep", "Profit");
    const revenueTrendMap = monthlySum(filteredRows, "Order_Date", "Revenue");
    const profitPerOrderByRegionMap = {};
    const ordersByRegionMap = groupByCount(filteredRows, "Region");
    const profitByRegionMap = groupBySum(filteredRows, "Region", "Profit");
    Object.keys(profitByRegionMap).forEach((region) => {
      profitPerOrderByRegionMap[region] = ordersByRegionMap[region] ? profitByRegionMap[region] / ordersByRegionMap[region] : 0;
    });

    const topProduct = topEntries(topProductMap, 1)[0]?.[0] || "-";
    const topRegion = topEntries(topRegionMap, 1)[0]?.[0] || "-";
    const topSalesRep = topEntries(revenueByRepMap, 1)[0]?.[0] || "-";
    const strongestChannel = topEntries(salesByChannelMap, 1)[0]?.[0] || "-";

    return {
      totalRevenue,
      totalProfit,
      profitMargin,
      totalOrders,
      avgOrderValue,
      profitPerOrder,
      discountImpact,
      avgDiscount,
      topProductMap,
      productProfitMap,
      quantityByProductMap,
      topRegionMap,
      salesByChannelMap,
      revenueByRepMap,
      profitByRepMap,
      revenueTrendMap,
      profitPerOrderByRegionMap,
      topProduct,
      topRegion,
      topSalesRep,
      strongestChannel,
    };
  }, [dashboardType, filteredRows]);

  const financeData = useMemo(() => {
    if (dashboardType !== "finance") return null;

    const totalRevenue = filteredRows.reduce((sum, row) => sum + Number(row.Revenue || 0), 0);
    const totalExpense = filteredRows.reduce((sum, row) => sum + Number(row.Expense || 0), 0);
    const netProfit = filteredRows.reduce((sum, row) => sum + Number(row.Profit || 0), 0);
    const totalBudget = filteredRows.reduce((sum, row) => sum + Number(row.Budget || 0), 0);
    const totalActual = filteredRows.reduce((sum, row) => sum + Number(row.Actual || 0), 0);
    const cashFlow = totalRevenue - totalExpense;
    const profitMargin = totalRevenue ? (netProfit / totalRevenue) * 100 : 0;
    const expenseRatio = totalRevenue ? (totalExpense / totalRevenue) * 100 : 0;
    const budgetVariance = totalActual - totalBudget;

    const expenseByCategoryMap = groupBySum(filteredRows, "Category", "Expense");
    const profitByDepartmentMap = groupBySum(filteredRows, "Department", "Profit");
    const profitByCategoryMap = groupBySum(filteredRows, "Category", "Profit");
    const actualTrendMap = monthlySum(filteredRows, "Date", "Actual");
    const varianceByDepartmentMap = {};
    const paymentStatusMap = groupByCount(filteredRows, "Payment_Status");
    const receivedCount = filteredRows.filter((row) => ["received", "paid", "complete", "completed"].includes(String(row.Payment_Status || "").toLowerCase())).length;
    const paymentRealizationRate = filteredRows.length ? (receivedCount / filteredRows.length) * 100 : 0;

    filteredRows.forEach((row) => {
      const dept = row.Department || "Unknown";
      varianceByDepartmentMap[dept] = (varianceByDepartmentMap[dept] || 0) + (Number(row.Actual || 0) - Number(row.Budget || 0));
    });

    const largestExpenseCategory = topEntries(expenseByCategoryMap, 1)[0]?.[0] || "-";
    const bestDepartment = topEntries(profitByDepartmentMap, 1)[0]?.[0] || "-";
    const mostProfitableCategory = topEntries(profitByCategoryMap, 1)[0]?.[0] || "-";
    const highestOverspendingDepartment = topEntries(varianceByDepartmentMap, 1)[0]?.[0] || "-";

    return {
      totalRevenue,
      totalExpense,
      netProfit,
      profitMargin,
      expenseRatio,
      totalBudget,
      totalActual,
      budgetVariance,
      cashFlow,
      expenseByCategoryMap,
      profitByDepartmentMap,
      profitByCategoryMap,
      varianceByDepartmentMap,
      paymentStatusMap,
      paymentRealizationRate,
      actualTrendMap,
      largestExpenseCategory,
      bestDepartment,
      mostProfitableCategory,
      highestOverspendingDepartment,
    };
  }, [dashboardType, filteredRows]);

  const marketingData = useMemo(() => {
    if (dashboardType !== "marketing") return null;

    const totalSpend = filteredRows.reduce((sum, row) => sum + Number(row.Spend || 0), 0);
    const totalRevenue = filteredRows.reduce((sum, row) => sum + Number(row.Revenue || 0), 0);
    const totalImpressions = filteredRows.reduce((sum, row) => sum + Number(row.Impressions || 0), 0);
    const totalClicks = filteredRows.reduce((sum, row) => sum + Number(row.Clicks || 0), 0);
    const totalLeads = filteredRows.reduce((sum, row) => sum + Number(row.Leads || 0), 0);
    const totalConversions = filteredRows.reduce((sum, row) => sum + Number(row.Conversions || 0), 0);
    const avgCtr = totalImpressions ? (totalClicks / totalImpressions) * 100 : 0;
    const conversionRate = totalLeads ? (totalConversions / totalLeads) * 100 : 0;
    const clickToConversionRate = totalClicks ? (totalConversions / totalClicks) * 100 : 0;
    const costPerLead = totalLeads ? totalSpend / totalLeads : 0;
    const roi = totalSpend ? ((totalRevenue - totalSpend) / totalSpend) * 100 : 0;

    const revenueBySourceMap = groupBySum(filteredRows, "Source", "Revenue");
    const leadsBySourceMap = groupBySum(filteredRows, "Source", "Leads");
    const spendByChannelMap = groupBySum(filteredRows, "Channel", "Spend");
    const revenueByChannelMap = groupBySum(filteredRows, "Channel", "Revenue");
    const conversionsByCampaignMap = groupBySum(filteredRows, "Campaign_Name", "Conversions");
    const revenueByCampaignMap = groupBySum(filteredRows, "Campaign_Name", "Revenue");
    const revenueTrendMap = monthlySum(filteredRows, "Date", "Revenue");
    const roiByChannelMap = {};
    Object.keys(revenueByChannelMap).forEach((channel) => {
      const spend = spendByChannelMap[channel] || 0;
      roiByChannelMap[channel] = spend ? ((revenueByChannelMap[channel] - spend) / spend) * 100 : 0;
    });
    const ctrBySourceMap = ratioByGroup(filteredRows, "Source", "Clicks", "Impressions");
    const conversionRateBySourceMap = ratioByGroup(filteredRows, "Source", "Conversions", "Leads");
    const costPerLeadBySourceMap = {};
    Object.keys(leadsBySourceMap).forEach((source) => {
      const spend = filteredRows.filter((row) => (row.Source || "Unknown") === source).reduce((sum, row) => sum + Number(row.Spend || 0), 0);
      costPerLeadBySourceMap[source] = leadsBySourceMap[source] ? spend / leadsBySourceMap[source] : 0;
    });
    const clickToConversionByCampaignMap = ratioByGroup(filteredRows, "Campaign_Name", "Conversions", "Clicks");

    const topSource = topEntries(revenueBySourceMap, 1)[0]?.[0] || "-";
    const topCampaign = topEntries(revenueByCampaignMap, 1)[0]?.[0] || topEntries(conversionsByCampaignMap, 1)[0]?.[0] || "-";
    const strongestChannel = topEntries(roiByChannelMap, 1)[0]?.[0] || "-";

    return {
      totalSpend,
      totalRevenue,
      totalImpressions,
      totalClicks,
      totalLeads,
      totalConversions,
      avgCtr,
      conversionRate,
      clickToConversionRate,
      costPerLead,
      roi,
      revenueBySourceMap,
      leadsBySourceMap,
      spendByChannelMap,
      revenueByChannelMap,
      conversionsByCampaignMap,
      revenueByCampaignMap,
      revenueTrendMap,
      roiByChannelMap,
      ctrBySourceMap,
      conversionRateBySourceMap,
      costPerLeadBySourceMap,
      clickToConversionByCampaignMap,
      topSource,
      topCampaign,
      strongestChannel,
    };
  }, [dashboardType, filteredRows]);

const getStoryCards = () => {
    if (dashboardType === "hr" && hrData) {
      return [
        { title: "Workforce Size", text: `${formatNumber(hrData.totalEmployees)} employees are in the current view. This gives leadership a clear workforce planning base.` },
        { title: "Retention Risk", text: `Attrition is ${formatPercent(hrData.attritionRate)}. ${hrData.highestAttritionDepartment} needs the closest retention review.` },
        { title: "Hiring Efficiency", text: `Hiring conversion is ${formatPercent(hrData.hiringConversionRate)} based on employee count against applicant volume. Low conversion means screening or sourcing needs improvement.` },
        { title: "Compensation Signal", text: `Average salary is ${formatCurrency(hrData.avgSalary)}. Review salary by experience level to detect pay imbalance.` },
      ];
    }

    if (dashboardType === "sales" && salesData) {
      return [
        { title: "Revenue Story", text: `${formatCurrency(salesData.totalRevenue)} sales revenue across ${formatNumber(salesData.totalOrders)} orders.` },
        { title: "Profitability", text: `Total profit is ${formatCurrency(salesData.totalProfit)} with ${formatPercent(salesData.profitMargin)} margin.` },
        { title: "Discount Control", text: `Estimated discount impact is ${formatCurrency(salesData.discountImpact)}. Watch discount-heavy deals before scaling.` },
        { title: "Growth Focus", text: `${salesData.topProduct} and ${salesData.topRegion} are the strongest sales drivers.` },
      ];
    }

    if (dashboardType === "finance" && financeData) {
      return [
        { title: "Financial Scale", text: `${formatCurrency(financeData.totalRevenue)} revenue and ${formatCurrency(financeData.netProfit)} profit are visible in this report.` },
        { title: "Efficiency", text: `Profit margin is ${formatPercent(financeData.profitMargin)} and expense ratio is ${formatPercent(financeData.expenseRatio)}.` },
        { title: "Budget Control", text: `Budget variance is ${formatCurrency(financeData.budgetVariance)}. ${financeData.highestOverspendingDepartment} needs budget review.` },
        { title: "Cash Health", text: `Payment realization is ${formatPercent(financeData.paymentRealizationRate)}. Improve collection discipline if this drops.` },
      ];
    }

    if (dashboardType === "marketing" && marketingData) {
      return [
        { title: "Campaign Investment", text: `${formatCurrency(marketingData.totalSpend)} spent generated ${formatCurrency(marketingData.totalRevenue)} revenue.` },
        { title: "Campaign ROI", text: `Overall ROI is ${formatPercent(marketingData.roi)}. ${marketingData.strongestChannel} is currently the best channel by ROI.` },
        { title: "Audience Engagement", text: `CTR is ${formatPercent(marketingData.avgCtr)} across ${formatNumber(marketingData.totalImpressions)} impressions.` },
        { title: "Lead Quality", text: `Conversion rate is ${formatPercent(marketingData.conversionRate)} and cost per lead is ${formatCurrency(marketingData.costPerLead)}.` },
      ];
    }

    return [];
  };

  const getKpiDriverCards = () => {
    if (dashboardType === "hr" && hrData) {
      return [
        { title: "Total Employees", value: formatNumber(hrData.totalEmployees), note: "Count(Employee_ID) • workforce planning" },
        { title: "Attrition Rate", value: formatPercent(hrData.attritionRate), note: "Exit_Date / Total Employees • retention risk" },
        { title: "Highest Attrition Dept", value: hrData.highestAttritionDepartment, note: "Attrition grouped by Department" },
        { title: "Hiring Conversion", value: formatPercent(hrData.hiringConversionRate), note: "Hired employees / Applicants_Count" },
        { title: "Average Salary", value: formatCurrency(hrData.avgSalary), note: "AVG(Salary_USD) • compensation benchmark" },
        { title: "Top Applicant Work Mode", value: hrData.topWorkModeByApplicants, note: "Applicants per Work Mode" },
        { title: "Largest Department", value: hrData.largestDepartment, note: "Employee distribution • allocation check" },
        { title: "Open Positions", value: formatNumber(hrData.openPositions), note: "Current hiring demand" },
      ];
    }

    if (dashboardType === "sales" && salesData) {
      return [
        { title: "Total Sales Revenue", value: formatCurrency(salesData.totalRevenue), note: "Total generated revenue" },
        { title: "Total Profit", value: formatCurrency(salesData.totalProfit), note: "Total earned profit" },
        { title: "Discount Impact", value: formatCurrency(salesData.discountImpact), note: "Shows margin pressure from discounts" },
        { title: "Average Order Value", value: formatCurrency(salesData.avgOrderValue), note: "Average value per order" },
        { title: "Top Product", value: salesData.topProduct, note: "Strongest product performer" },
        { title: "Top Region", value: salesData.topRegion, note: "Strongest market area" },
        { title: "Best Sales Rep", value: salesData.topSalesRep, note: "Sales rep performance" },
        { title: "Profit per Order", value: formatCurrency(salesData.profitPerOrder), note: "Profit earned per order" },
      ];
    }

    if (dashboardType === "finance" && financeData) {
      return [
        { title: "Total Revenue", value: formatCurrency(financeData.totalRevenue), note: "Total generated revenue" },
        { title: "Total Profit", value: formatCurrency(financeData.netProfit), note: "Total earned profit" },
        { title: "Profit Margin", value: formatPercent(financeData.profitMargin), note: "Profit / Revenue" },
        { title: "Expense Ratio", value: formatPercent(financeData.expenseRatio), note: "Expense / Revenue" },
        { title: "Budget vs Actual", value: `${formatCurrency(financeData.totalBudget)} / ${formatCurrency(financeData.totalActual)}`, note: "Budget control" },
        { title: "Highest Variance Dept", value: financeData.highestOverspendingDepartment, note: "Actual - Budget" },
        { title: "Payment Realization", value: formatPercent(financeData.paymentRealizationRate), note: "Received / Total" },
        { title: "Best Category", value: financeData.mostProfitableCategory, note: "Profit by Category" },
      ];
    }

    if (dashboardType === "marketing" && marketingData) {
      return [
        { title: "Total Spend", value: formatCurrency(marketingData.totalSpend), note: "SUM(Spend)" },
        { title: "ROI", value: formatPercent(marketingData.roi), note: "(Revenue - Spend) / Spend" },
        { title: "CTR", value: formatPercent(marketingData.avgCtr), note: "Clicks / Impressions" },
        { title: "Conversion Rate", value: formatPercent(marketingData.conversionRate), note: "Conversions / Leads" },
        { title: "Cost per Lead", value: formatCurrency(marketingData.costPerLead), note: "Spend / Leads" },
        { title: "Best Channel", value: marketingData.strongestChannel, note: "ROI by Channel" },
        { title: "Click to Conversion", value: formatPercent(marketingData.clickToConversionRate), note: "Conversions / Clicks" },
        { title: "Scale Campaign", value: marketingData.topCampaign, note: "Revenue per Campaign" },
      ];
    }

    return [];
  };

  const exportPDF = async () => {
    if (!hasPremiumAccess) {
      setMessage("Premium subscription required to export PDF.");
      return;
    }

    const root = exportRef.current;
    if (!root) return;

    try {
      setMessage("Preparing client-ready PDF report...");

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = 210;
      const pdfHeight = 297;
      const margin = 8;
      const usableWidth = pdfWidth - margin * 2;
      const usableHeight = pdfHeight - margin * 2;
      let y = margin;
      let hasContent = false;

      const visibleBlocks = Array.from(root.children).filter((el) => {
        const style = window.getComputedStyle(el);
        return style.display !== "none" && style.visibility !== "hidden" && el.offsetHeight > 0;
      });

      const blocks = visibleBlocks.length ? visibleBlocks : [root];

      for (const block of blocks) {
        const canvas = await html2canvas(block, {
          scale: 1.25,
          useCORS: true,
          backgroundColor: currentTheme.pageBackground || "#ffffff",
          logging: false,
          windowWidth: 1440,
        });

        if (!canvas.width || !canvas.height) continue;

        const imgData = canvas.toDataURL("image/png");
        const imgHeight = (canvas.height * usableWidth) / canvas.width;

        if (imgHeight <= usableHeight) {
          if (hasContent && y + imgHeight > pdfHeight - margin) {
            pdf.addPage();
            y = margin;
          }
          pdf.addImage(imgData, "PNG", margin, y, usableWidth, imgHeight);
          y += imgHeight + 6;
          hasContent = true;
        } else {
          // Large section fallback: split this section safely across pages.
          let heightLeft = imgHeight;
          let position = margin;
          if (hasContent) {
            pdf.addPage();
          }
          pdf.addImage(imgData, "PNG", margin, position, usableWidth, imgHeight);
          heightLeft -= usableHeight;

          while (heightLeft > 0) {
            pdf.addPage();
            position = heightLeft - imgHeight + margin;
            pdf.addImage(imgData, "PNG", margin, position, usableWidth, imgHeight);
            heightLeft -= usableHeight;
          }
          y = margin;
          hasContent = true;
        }
      }

      pdf.save(`Bivora_Client_Report_${dashboardType || "Dashboard"}.pdf`);
      setMessage("Client-ready PDF downloaded successfully.");
    } catch (error) {
      console.error("PDF export error:", error);
      setMessage("PDF export failed. Please try again after closing other tabs or reducing zoom.");
    }
  };


  const getPrimaryDateKey = () => {
    if (dashboardType === "sales") return "Order_Date";
    if (dashboardType === "finance") return "Date";
    if (dashboardType === "marketing") return "Date";
    if (dashboardType === "hr") return "Hire_Date";
    return "Date";
  };

  const getPrimaryValueKey = () => {
    if (dashboardType === "hr") return "Salary_USD";
    if (dashboardType === "marketing") return "Revenue";
    if (dashboardType === "finance") return "Revenue";
    return "Revenue";
  };

  const calculateSimpleTotals = (dataRows = filteredRows) => {
    const totalRevenue = dataRows.reduce((sum, row) => sum + Number(row.Revenue || row.Net_Sales || row.Gross_Sales || 0), 0);
    const totalProfit = dataRows.reduce((sum, row) => sum + Number(row.Profit || 0), 0);
    const totalExpense = dataRows.reduce((sum, row) => sum + Number(row.Expense || 0), 0);
    const totalSpend = dataRows.reduce((sum, row) => sum + Number(row.Spend || 0), 0);
    const totalEmployees = dataRows.length;
    return { totalRevenue, totalProfit, totalExpense, totalSpend, totalEmployees };
  };

  const calculatePeriodTrend = (dataRows = filteredRows, dateKey = getPrimaryDateKey(), valueKey = getPrimaryValueKey()) => {
    const monthly = monthlySum(dataRows, dateKey, valueKey);
    const labels = Object.keys(monthly).sort();
    if (labels.length < 2) return { current: labels[0] ? monthly[labels[0]] : 0, previous: 0, pct: 0, direction: "flat" };
    const previous = monthly[labels[labels.length - 2]] || 0;
    const current = monthly[labels[labels.length - 1]] || 0;
    const pct = previous ? ((current - previous) / previous) * 100 : 0;
    return { current, previous, pct, direction: pct > 0 ? "up" : pct < 0 ? "down" : "flat" };
  };

  const forecastNextRevenue = () => {
    const dateKey = getPrimaryDateKey();
    const valueKey = getPrimaryValueKey();
    const monthly = monthlySum(filteredRows, dateKey, valueKey);
    const labels = Object.keys(monthly).sort().slice(-6);

    if (!labels.length) {
      return {
        previous: 0,
        current: 0,
        nextValue: 0,
        changeAmount: 0,
        cappedPct: 0,
        direction: "flat",
        confidence: "Low",
        note: "Upload or load data to forecast.",
        labels: [],
      };
    }

    if (labels.length === 1) {
      const current = Number(monthly[labels[0]] || 0);
      return {
        previous: current,
        current,
        nextValue: current,
        changeAmount: 0,
        cappedPct: 0,
        direction: "flat",
        confidence: "Medium",
        note: "Only one period found. Forecast uses current run-rate.",
        labels,
      };
    }

    const current = Number(monthly[labels[labels.length - 1]] || 0);
    const previous = Number(monthly[labels[labels.length - 2]] || 0);
    const rawGrowth = previous ? (current - previous) / previous : 0;
    const cappedGrowth = Math.max(Math.min(rawGrowth, 0.3), -0.3);
    const nextValue = Math.round(current * (1 + cappedGrowth));

    return {
      previous,
      current,
      nextValue,
      changeAmount: nextValue - current,
      cappedPct: cappedGrowth * 100,
      direction: cappedGrowth > 0 ? "growth" : cappedGrowth < 0 ? "decline" : "flat",
      confidence: labels.length >= 4 ? "Good" : "Medium",
      note: `${cappedGrowth >= 0 ? "Growth" : "Decline"} projection from latest monthly trend. Forecast is capped at ±30% for safer planning.`,
      labels,
    };
  };

  const generateSmartAlerts = () => {
    const alerts = [];
    const trend = calculatePeriodTrend();
    const totals = calculateSimpleTotals();

    if (dashboardType === "sales") {
      if (trend.pct < -10) alerts.push({ type: "risk", text: `Revenue dropped ${formatPercent(Math.abs(trend.pct))} in the latest period. Review regions, products, and sales reps.` });
      if (salesData?.profitMargin < 10) alerts.push({ type: "risk", text: `Profit margin is ${formatPercent(salesData.profitMargin)}. Discounting or cost pressure may be hurting sales quality.` });
      if (salesData?.topRegion) alerts.push({ type: "success", text: `${salesData.topRegion} is the best regional performer. Use it as a benchmark for other regions.` });
      if (salesData?.avgDiscount > 10) alerts.push({ type: "warning", text: `Average discount is ${formatPercent(salesData.avgDiscount)}. Check whether discounts are reducing profit.` });
    }

    if (dashboardType === "hr") {
      if (hrData?.attritionRate > 15) alerts.push({ type: "risk", text: `Attrition is ${formatPercent(hrData.attritionRate)}. Retention action is needed, especially in ${hrData.highestAttritionDepartment}.` });
      if (hrData?.topWorkModeByApplicants) alerts.push({ type: "success", text: `${hrData.topWorkModeByApplicants} roles attract the most applicants. Use this insight for hiring strategy.` });
      if (hrData?.avgSalary) alerts.push({ type: "info", text: `Average salary is ${formatCurrency(hrData.avgSalary)}. Compare by experience level for pay balance.` });
    }

    if (dashboardType === "finance") {
      if (financeData?.expenseRatio > 60) alerts.push({ type: "risk", text: `Expense ratio is ${formatPercent(financeData.expenseRatio)}. Cost control should be prioritized.` });
      if ((financeData?.totalActual || 0) > (financeData?.totalBudget || 0)) alerts.push({ type: "warning", text: `Actual spending is above budget. Review variance by department.` });
      if (financeData?.paymentRealizationRate < 70) alerts.push({ type: "risk", text: `Payment realization is ${formatPercent(financeData.paymentRealizationRate)}. Cash collection needs attention.` });
    }

    if (dashboardType === "marketing") {
      if (marketingData?.roi > 30) alerts.push({ type: "success", text: `Campaign ROI is strong at ${formatPercent(marketingData.roi)}. Scale high-performing channels carefully.` });
      if (marketingData?.conversionRate < 5) alerts.push({ type: "warning", text: `Conversion rate is ${formatPercent(marketingData.conversionRate)}. Improve landing pages, offer, or targeting.` });
      if (marketingData?.costPerLead > 50) alerts.push({ type: "warning", text: `Cost per lead is ${formatCurrency(marketingData.costPerLead)}. Review expensive sources.` });
    }

    if (!alerts.length && filteredRows.length) {
      alerts.push({ type: "success", text: `No major risk detected in the current filtered view. Continue monitoring trends and KPI drivers.` });
    }

    if (!filteredRows.length) {
      alerts.push({ type: "warning", text: "No rows match the current filters. Clear filters or load a report." });
    }

    return alerts.slice(0, 5);
  };

  const answerAIQuestion = () => {
    const q = aiQuestion.trim();
    if (!q) return;
    const query = q.toLowerCase();
    let answer = "I can answer questions about revenue, profit, region, employee, department, campaign, source, salary, attrition, budget, and forecast based on the current dashboard data.";

    if (!filteredRows.length) {
      answer = "No data is currently available in the selected dashboard view. Upload or load a report first.";
    } else if (query.includes("why") && (query.includes("sales") || query.includes("revenue")) && query.includes("drop")) {
      const trend = calculatePeriodTrend(filteredRows, getPrimaryDateKey(), "Revenue");
      const regionMap = groupBySum(filteredRows, "Region", "Revenue");
      const weakestRegion = topEntries(regionMap, 999).reverse()[0]?.[0] || "a low-performing region";
      answer = trend.pct < 0
        ? `Revenue is down ${formatPercent(Math.abs(trend.pct))} in the latest period. The first area to review is ${weakestRegion}, then check product mix, discounts, and sales rep performance.`
        : `Revenue is not showing a drop in the latest period. Current trend is ${formatPercent(trend.pct)}. Review region and product mix for deeper causes.`;
    } else if (query.includes("top") && query.includes("region")) {
      const regionMap = groupBySum(filteredRows, "Region", "Revenue");
      const top = topEntries(regionMap, 1)[0];
      answer = top ? `${top[0]} is the top region with ${formatCurrency(top[1])} revenue. Focus on what is working there and replicate it in weaker regions.` : "Region data is not available.";
    } else if (query.includes("employee") || query.includes("best employee")) {
      if (dashboardType === "hr") {
        const applicantMap = groupBySum(filteredRows, "Job_Title", "Applicants_Count");
        const role = topEntries(applicantMap, 1)[0];
        answer = role ? `This HR dataset does not rank individual performance, but ${role[0]} has the strongest hiring demand with ${formatNumber(role[1])} applicants.` : "Employee-level performance data is not available in this HR file.";
      } else if (dashboardType === "sales") {
        const repMap = groupBySum(filteredRows, "Sales_Rep", "Revenue");
        const top = topEntries(repMap, 1)[0];
        answer = top ? `${top[0]} is the best sales performer by revenue with ${formatCurrency(top[1])}. Use this rep's territory/process as a benchmark.` : "Sales rep data is not available.";
      }
    } else if (query.includes("profit")) {
      const profit = filteredRows.reduce((sum, row) => sum + Number(row.Profit || 0), 0);
      const revenue = filteredRows.reduce((sum, row) => sum + Number(row.Revenue || row.Net_Sales || row.Gross_Sales || 0), 0);
      const margin = revenue ? (profit / revenue) * 100 : 0;
      answer = `Total profit is ${formatCurrency(profit)} with ${formatPercent(margin)} margin. If margin is low, review discounts, costs, and low-profit categories.`;
    } else if (query.includes("forecast") || query.includes("next month")) {
      const forecast = forecastNextRevenue();
      answer = `Projected next period value is ${formatCurrency(forecast.nextValue)}. ${forecast.note}`;
    } else if (query.includes("campaign") || query.includes("source")) {
      if (dashboardType === "marketing") {
        const sourceMap = groupBySum(filteredRows, "Source", "Revenue");
        const campaignMap = groupBySum(filteredRows, "Campaign_Name", "Revenue");
        const topSource = topEntries(sourceMap, 1)[0];
        const topCampaign = topEntries(campaignMap, 1)[0];
        answer = `${topSource?.[0] || "Top source"} is leading by revenue. ${topCampaign?.[0] || "Top campaign"} should be reviewed for scaling because it has the strongest campaign contribution.`;
      }
    } else if (query.includes("department")) {
      const deptKey = dashboardType === "finance" ? "Department" : "Department";
      const valueKey = dashboardType === "finance" ? "Profit" : dashboardType === "hr" ? "Salary_USD" : "Revenue";
      const deptMap = groupBySum(filteredRows, deptKey, valueKey);
      const top = topEntries(deptMap, 1)[0];
      answer = top ? `${top[0]} is the leading department for this view. Its contribution is ${formatCurrency(top[1])}.` : "Department data is not available.";
    }

    setAiChatMessages((prev) => [{ question: q, answer, time: new Date().toLocaleTimeString() }, ...prev].slice(0, 8));
    setAiQuestion("");
  };

  const parseSpreadsheetRows = (arrayBuffer) => {
    const workbook = XLSX.read(arrayBuffer, { type: "array" });
    const firstSheet = workbook.SheetNames[0];
    if (!firstSheet) return [];
    const worksheet = workbook.Sheets[firstSheet];
    const parsed = XLSX.utils.sheet_to_json(worksheet, { defval: "", raw: false });

    return parsed.map((row) => {
      const normalized = {};
      Object.keys(row || {}).forEach((key) => {
        const cleanKey = String(key || "").trim();
        const raw = row[key];
        const numeric = Number(String(raw).replace(/[^0-9.-]/g, ""));
        normalized[cleanKey] = raw !== "" && Number.isFinite(numeric) && /[0-9]/.test(String(raw)) ? numeric : raw;
      });
      return normalized;
    });
  };

  const handleCompareFile = (file, slot) => {
    if (!file) return;

    const validExt = /\.(csv|xlsx|xls)$/i.test(file.name);
    if (!validExt) {
      setMessage("Please upload CSV or Excel file for comparison.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsed = parseSpreadsheetRows(event.target.result);
        if (slot === "A") {
          setCompareRowsA(parsed);
          setCompareFileAName(file.name);
        } else {
          setCompareRowsB(parsed);
          setCompareFileBName(file.name);
        }
        setMessage(`${file.name} loaded for comparison.`);
      } catch (error) {
        console.error("Comparison file error:", error);
        setMessage("Could not read comparison file. Please use a clean CSV or Excel sheet.");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const compareSummary = () => {
    const sumMetric = (dataRows, keys) =>
      dataRows.reduce((sum, row) => sum + Number(keys.map((key) => row[key]).find((value) => value !== undefined && value !== "") || 0), 0);

    const aRevenue = sumMetric(compareRowsA, ["Revenue", "Net_Sales", "Gross_Sales", "Actual"]);
    const bRevenue = sumMetric(compareRowsB, ["Revenue", "Net_Sales", "Gross_Sales", "Actual"]);
    const aProfit = sumMetric(compareRowsA, ["Profit"]);
    const bProfit = sumMetric(compareRowsB, ["Profit"]);
    const aCost = sumMetric(compareRowsA, ["Cost", "Expense", "Spend"]);
    const bCost = sumMetric(compareRowsB, ["Cost", "Expense", "Spend"]);
    const diff = bRevenue - aRevenue;
    const pct = aRevenue ? (diff / aRevenue) * 100 : 0;
    const profitDiff = bProfit - aProfit;
    const costDiff = bCost - aCost;

    return {
      a: aRevenue,
      b: bRevenue,
      diff,
      pct,
      aProfit,
      bProfit,
      profitDiff,
      aCost,
      bCost,
      costDiff,
      aRows: compareRowsA.length,
      bRows: compareRowsB.length,
    };
  };

  const getComparisonInsights = () => {
    const c = compareSummary();
    if (!compareRowsA.length || !compareRowsB.length) {
      return ["Upload two CSV or Excel files to see comparison insights."];
    }

    const insights = [];
    insights.push(c.pct >= 0 ? `Revenue increased by ${formatPercent(c.pct)} from File A to File B.` : `Revenue declined by ${formatPercent(Math.abs(c.pct))} from File A to File B.`);
    insights.push(c.profitDiff >= 0 ? `Profit improved by ${formatCurrency(c.profitDiff)}.` : `Profit reduced by ${formatCurrency(Math.abs(c.profitDiff))}; review costs, discounts, or low-margin areas.`);
    insights.push(c.costDiff > 0 ? `Cost/expense increased by ${formatCurrency(c.costDiff)}. Check whether the revenue growth justifies the extra spend.` : `Cost/expense reduced by ${formatCurrency(Math.abs(c.costDiff))}, which supports margin improvement.`);
    insights.push(c.bRows >= c.aRows ? `File B has ${formatNumber(c.bRows - c.aRows)} more rows/transactions than File A.` : `File B has ${formatNumber(c.aRows - c.bRows)} fewer rows/transactions than File A.`);
    return insights;
  };

  const guessConnectedField = (fields, aliases = []) => {
    const normalized = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const normalizedFields = fields.map((field) => ({ field, key: normalized(field) }));
    for (const alias of aliases) {
      const aliasKey = normalized(alias);
      const exact = normalizedFields.find((item) => item.key === aliasKey);
      if (exact) return exact.field;
    }
    for (const alias of aliases) {
      const aliasKey = normalized(alias);
      const partial = normalizedFields.find((item) => item.key.includes(aliasKey) || aliasKey.includes(item.key));
      if (partial) return partial.field;
    }
    return "";
  };

  const buildAutoConnectedMapping = (fields = []) => ({
    Revenue: guessConnectedField(fields, ["Revenue", "Sales", "Amount", "Total", "Net Sales", "Gross Sales", "Deal Value", "Invoice Amount", "Order Value"]),
    Profit: guessConnectedField(fields, ["Profit", "Margin", "Net Profit", "Gross Profit", "Earnings"]),
    Cost: guessConnectedField(fields, ["Cost", "Expense", "Spend", "Ad Spend", "COGS", "Budget"]),
    Region: guessConnectedField(fields, ["Region", "Area", "Zone", "Location", "Territory", "State", "Country"]),
    Department: guessConnectedField(fields, ["Department", "Team", "Business Unit", "Cost Center", "Owner Department"]),
    Category: guessConnectedField(fields, ["Category", "Product Category", "Segment", "Industry", "Source", "Channel", "Type"]),
    Date: guessConnectedField(fields, ["Date", "Created Date", "Order Date", "Invoice Date", "Close Date", "Month"]),
  });

  const applyConnectedMapping = (rawRows = connectedRawRows, mapping = connectedFieldMapping) => {
    const mappedRows = (rawRows || []).map((row) => {
      const revenue = Number(String(row?.[mapping.Revenue] ?? 0).replace(/[^0-9.-]/g, "")) || 0;
      const explicitProfit = Number(String(row?.[mapping.Profit] ?? "").replace(/[^0-9.-]/g, ""));
      const cost = Number(String(row?.[mapping.Cost] ?? 0).replace(/[^0-9.-]/g, "")) || 0;
      const profit = Number.isFinite(explicitProfit) && String(row?.[mapping.Profit] ?? "") !== "" ? explicitProfit : revenue - cost;

      return {
        Revenue: revenue,
        Profit: profit,
        Cost: cost,
        Region: row?.[mapping.Region] || "Unknown",
        Department: row?.[mapping.Department] || "General",
        Category: row?.[mapping.Category] || "General",
        Date: row?.[mapping.Date] || "",
      };
    });

    setConnectedRows(mappedRows);
    return mappedRows;
  };

  const calculateConnectedKpis = (dataRows = connectedRows) => {
    const totalRevenue = dataRows.reduce((sum, row) => sum + Number(row.Revenue || 0), 0);
    const totalProfit = dataRows.reduce((sum, row) => sum + Number(row.Profit || 0), 0);
    const totalCost = dataRows.reduce((sum, row) => sum + Number(row.Cost || 0), 0);
    const margin = totalRevenue ? (totalProfit / totalRevenue) * 100 : 0;
    const regionMap = groupBySum(dataRows, "Region", "Revenue");
    const departmentMap = groupBySum(dataRows, "Department", "Profit");
    const categoryMap = groupBySum(dataRows, "Category", "Profit");
    const topRegion = topEntries(regionMap, 1)[0]?.[0] || "Not available";
    const topDepartment = topEntries(departmentMap, 1)[0]?.[0] || "Not available";
    const topCategory = topEntries(categoryMap, 1)[0]?.[0] || "Not available";
    return { totalRevenue, totalProfit, totalCost, margin, topRegion, topDepartment, topCategory, rows: dataRows.length };
  };

  const hydrateConnectedPayload = (payload, sourceName) => {
    const rowsPayload = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : Array.isArray(payload?.rows) ? payload.rows : [];
    if (!rowsPayload.length) {
      setMessage("Connected source responded, but no rows were found. Use an array, { data: [] }, or { rows: [] } format.");
      return false;
    }

    const fields = Object.keys(rowsPayload[0] || {});
    const autoMapping = buildAutoConnectedMapping(fields);
    setConnectedRawRows(rowsPayload);
    setConnectedFieldOptions(fields);
    setConnectedFieldMapping(autoMapping);
    applyConnectedMapping(rowsPayload, autoMapping);
    setConnectedDashboardType("connected");
    setConnectedDashboardName("Connected Connect Data");
    setConnectedSourceName(sourceName);
    setDashboardType("connected");
    setDashboardName("Connected Data Dashboard");
    setRows(rowsPayload);
    setActiveTab("connected");
    return true;
  };

  const loadApiData = async () => {
    if (!apiUrl.trim()) {
      setMessage("Please enter CRM/ERP/API/SQL endpoint URL first.");
      return;
    }
    try {
      setApiLoading(true);
      const res = await API.post("/connect-data/api", {
        url: apiUrl.trim(),
        apiKey: apiKey.trim(),
        authType: apiAuthType,
      });

      if (hydrateConnectedPayload(res.data?.payload || res.data?.rows || res.data, apiUrl.trim())) {
        setMessage("Connected Data page is ready. Review mapping, KPIs, and insights below.");
      }
    } catch (error) {
      setMessage(getErrorMessage(error, "Failed to connect data source. Check URL, API key, CORS/server access, or response format."));
    } finally {
      setApiLoading(false);
    }
  };

  const loadSqlFileData = async () => {
    if (!sqlFile) {
      setMessage("Please select a SQL/CSV/Excel exported data file first.");
      return;
    }

    const formData = new FormData();
    formData.append("file", sqlFile);

    try {
      setApiLoading(true);
      const token = localStorage.getItem("token");
      const res = await axios.post("http://localhost:5000/api/connect-data/file", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: token ? `Bearer ${token}` : "",
        },
        timeout: 30000,
      });

      if (hydrateConnectedPayload(res.data?.rows || [], sqlFile.name)) {
        setMessage("SQL/export file connected successfully. Review mapping and KPI output below.");
      }
    } catch (error) {
      setMessage(getErrorMessage(error, "Failed to read connected data file. Use CSV/XLSX/XLS exported from SQL/CRM/ERP."));
    } finally {
      setApiLoading(false);
    }
  };

  const printFullReport = () => {
    if (!hasPremiumAccess) {
      setMessage("Premium subscription required to print reports.");
      return;
    }

    if (!exportRef.current) return;

    const printWindow = window.open("", "_blank", "width=1400,height=900");
    if (!printWindow) return;

    const html = `
      <html>
        <head>
          <title>Bivora Full KPI Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 24px; background: #ffffff; color: #111827; }
            h1,h2,h3 { margin: 0 0 12px 0; }
            .print-wrap { width: 100%; }
            .print-wrap * { box-sizing: border-box; }
            .print-wrap canvas { max-width: 100% !important; }
          </style>
        </head>
        <body>
          <div class="print-wrap">${exportRef.current.innerHTML}</div>
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();

    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 800);
  };



  const getKpiStatus = (title, value) => {
    const numericValue = Number(String(value ?? "").replace(/[^0-9.-]/g, ""));
    const lower = String(title || "").toLowerCase();

    if (lower.includes("attrition") || lower.includes("expense ratio") || lower.includes("discount") || lower.includes("cost per lead")) {
      if (numericValue >= 25) return { label: "Risk", color: "#ef4444", bg: "rgba(239,68,68,0.12)" };
      if (numericValue >= 10) return { label: "Warning", color: "#f59e0b", bg: "rgba(245,158,11,0.12)" };
      return { label: "Healthy", color: "#10b981", bg: "rgba(16,185,129,0.12)" };
    }

    if (lower.includes("profit margin") || lower === "roi" || lower.includes("conversion") || lower.includes("ctr") || lower.includes("payment realization")) {
      if (numericValue >= 30) return { label: "Strong", color: "#10b981", bg: "rgba(16,185,129,0.12)" };
      if (numericValue >= 10) return { label: "Monitor", color: "#f59e0b", bg: "rgba(245,158,11,0.12)" };
      return { label: "Risk", color: "#ef4444", bg: "rgba(239,68,68,0.12)" };
    }

    if (lower.includes("profit") || lower.includes("revenue") || lower.includes("sales")) {
      if (numericValue > 0) return { label: "Positive", color: "#10b981", bg: "rgba(16,185,129,0.12)" };
      if (numericValue === 0) return { label: "Neutral", color: "#f59e0b", bg: "rgba(245,158,11,0.12)" };
      return { label: "Loss", color: "#ef4444", bg: "rgba(239,68,68,0.12)" };
    }

    return { label: "Info", color: "#6366f1", bg: "rgba(99,102,241,0.12)" };
  };

  const getTrendFromMap = (map, formatter = formatCurrency) => {
    const keys = Object.keys(map || {}).sort();
    if (keys.length < 2) return "No trend yet";
    const previous = Number(map[keys[keys.length - 2]] || 0);
    const latest = Number(map[keys[keys.length - 1]] || 0);
    if (!previous) return `Latest: ${formatter(latest)}`;
    const change = ((latest - previous) / Math.abs(previous)) * 100;
    const arrow = change >= 0 ? "↑" : "↓";
    return `${arrow} ${Math.abs(change).toFixed(1)}% vs previous period`;
  };

  const getCurrentTrendText = () => {
    if (dashboardType === "sales" && salesData) return getTrendFromMap(salesData.revenueTrendMap, formatCurrency);
    if (dashboardType === "finance" && financeData) return getTrendFromMap(financeData.actualTrendMap, formatCurrency);
    if (dashboardType === "marketing" && marketingData) return getTrendFromMap(marketingData.revenueTrendMap, formatCurrency);
    if (dashboardType === "hr" && hrData) return getTrendFromMap(hrData.salaryTrendMap, formatCurrency);
    return "Trend appears after multiple dated records";
  };

  const getDynamicExecutiveBullets = () => {
    if (dashboardType === "hr" && hrData) {
      return [
        { level: hrData.attritionRate >= 15 ? "risk" : hrData.attritionRate >= 8 ? "warning" : "good", text: `Attrition is ${formatPercent(hrData.attritionRate)}; ${hrData.highestAttritionDepartment} needs the closest retention review.` },
        { level: "info", text: `${formatNumber(hrData.totalEmployees)} employees are active in this report; largest workforce concentration is ${hrData.largestDepartment}.` },
        { level: hrData.hiringConversionRate < 10 ? "warning" : "good", text: `Hiring conversion is ${formatPercent(hrData.hiringConversionRate)}; improve screening if conversion is low.` },
        { level: "action", text: `Action: review pay fairness by experience level and prioritize roles with high applicant pressure.` },
      ];
    }

    if (dashboardType === "sales" && salesData) {
      return [
        { level: salesData.profitMargin < 10 ? "risk" : salesData.profitMargin < 20 ? "warning" : "good", text: `Profit margin is ${formatPercent(salesData.profitMargin)} on ${formatCurrency(salesData.totalRevenue)} revenue.` },
        { level: "info", text: `${salesData.topRegion} is the leading region and ${salesData.topProduct} is the strongest product.` },
        { level: salesData.avgDiscount > 10 ? "warning" : "good", text: `Average discount is ${formatPercent(salesData.avgDiscount)}; check whether discounting is reducing profit quality.` },
        { level: "action", text: `Action: scale high-margin products and replicate the playbook from ${salesData.topSalesRep}.` },
      ];
    }

    if (dashboardType === "finance" && financeData) {
      return [
        { level: financeData.profitMargin < 10 ? "risk" : financeData.profitMargin < 20 ? "warning" : "good", text: `Profit margin is ${formatPercent(financeData.profitMargin)} with expense ratio at ${formatPercent(financeData.expenseRatio)}.` },
        { level: financeData.budgetVariance > 0 ? "warning" : "good", text: `Budget variance is ${formatCurrency(financeData.budgetVariance)}; ${financeData.highestOverspendingDepartment} requires budget control.` },
        { level: financeData.paymentRealizationRate < 70 ? "risk" : financeData.paymentRealizationRate < 90 ? "warning" : "good", text: `Payment realization is ${formatPercent(financeData.paymentRealizationRate)}; monitor cash collection discipline.` },
        { level: "action", text: `Action: protect ${financeData.mostProfitableCategory} and reduce avoidable expenses in ${financeData.largestExpenseCategory}.` },
      ];
    }

    if (dashboardType === "marketing" && marketingData) {
      return [
        { level: marketingData.roi < 20 ? "risk" : marketingData.roi < 50 ? "warning" : "good", text: `Marketing ROI is ${formatPercent(marketingData.roi)} from ${formatCurrency(marketingData.totalSpend)} spend.` },
        { level: marketingData.avgCtr < 2 ? "warning" : "good", text: `CTR is ${formatPercent(marketingData.avgCtr)} and conversion rate is ${formatPercent(marketingData.conversionRate)}.` },
        { level: "info", text: `${marketingData.strongestChannel} has the best ROI and ${marketingData.topCampaign} is the campaign to study for scale.` },
        { level: "action", text: `Action: shift budget toward high-ROI channels and reduce CPL where lead quality is weak.` },
      ];
    }

    return [];
  };

  const bulletStyle = (level) => {
    const palette = {
      good: { bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.45)", color: "#10b981" },
      warning: { bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.45)", color: "#f59e0b" },
      risk: { bg: "rgba(239,68,68,0.12)", border: "rgba(239,68,68,0.45)", color: "#ef4444" },
      action: { bg: "rgba(99,102,241,0.12)", border: "rgba(99,102,241,0.45)", color: "#6366f1" },
      info: { bg: currentTheme.secondaryBg, border: currentTheme.border, color: currentTheme.text },
    };
    const p = palette[level] || palette.info;
    return {
      padding: "12px 14px",
      borderRadius: 14,
      border: `1px solid ${p.border}`,
      background: p.bg,
      color: currentTheme.text,
      fontSize: 14,
      lineHeight: 1.55,
      display: "flex",
      gap: 10,
      alignItems: "flex-start",
    };
  };

  const renderHeroKpis = () => {
    const cards = getKpiDriverCards().slice(0, 8);
    if (!cards.length) return null;
    const trendText = getCurrentTrendText();
    return (
      <div style={s.kpiGrid}>
        {cards.map((card) => {
          const status = getKpiStatus(card.title, card.value);
          return (
            <MiniKpi
              key={card.title}
              theme={currentTheme}
              title={card.title}
              value={card.value}
              status={status}
              trend={trendText}
            />
          );
        })}
      </div>
    );
  };

  const getAIInsightCards = () => {
    if (dashboardType === "hr" && hrData) {
      return [
        { title: "AI Insight: Retention Risk", text: `Attrition is ${formatPercent(hrData.attritionRate)}. Prioritize ${hrData.highestAttritionDepartment} for targeted retention action.` },
        { title: "AI Insight: Hiring Focus", text: `${hrData.mostDemandRole} has the highest applicant pressure. Improve screening speed and interview capacity.` },
        { title: "AI Insight: Pay Balance", text: `Average salary is ${formatCurrency(hrData.avgSalary)}. Compare salary by experience before revising compensation bands.` },
        { title: "Recommended Action", text: `Review attrition, salary fairness, and remote demand together before adding headcount.` },
      ];
    }

    if (dashboardType === "sales" && salesData) {
      return [
        { title: "AI Insight: Revenue Driver", text: `${salesData.topProduct} and ${salesData.topRegion} are leading sales performance.` },
        { title: "AI Insight: Margin Quality", text: `Profit margin is ${formatPercent(salesData.profitMargin)}. Track discounts before scaling campaigns.` },
        { title: "AI Insight: Sales Rep Focus", text: `${salesData.topSalesRep} is the top revenue contributor. Use this as a performance benchmark.` },
        { title: "Recommended Action", text: `Grow high-margin products, protect discount leakage, and replicate top-region strategy.` },
      ];
    }

    if (dashboardType === "finance" && financeData) {
      return [
        { title: "AI Insight: Profit Health", text: `Profit margin is ${formatPercent(financeData.profitMargin)} with expense ratio at ${formatPercent(financeData.expenseRatio)}.` },
        { title: "AI Insight: Budget Control", text: `${financeData.highestOverspendingDepartment} shows the highest variance and needs review.` },
        { title: "AI Insight: Category Performance", text: `${financeData.mostProfitableCategory} is currently the strongest profit category.` },
        { title: "Recommended Action", text: `Improve payment realization, review expense-heavy areas, and monitor variance monthly.` },
      ];
    }

    if (dashboardType === "marketing" && marketingData) {
      return [
        { title: "AI Insight: Channel ROI", text: `${marketingData.strongestChannel} is the strongest ROI channel. Consider scaling it first.` },
        { title: "AI Insight: Lead Quality", text: `Conversion rate is ${formatPercent(marketingData.conversionRate)} and CPL is ${formatCurrency(marketingData.costPerLead)}.` },
        { title: "AI Insight: Campaign Scaling", text: `${marketingData.topCampaign} is the best campaign to study for scaling.` },
        { title: "Recommended Action", text: `Shift budget from weak channels to high ROI campaigns and monitor click-to-conversion efficiency.` },
      ];
    }

    return [];
  };

  const renderKpiDriverSection = () => {
    const cards = getKpiDriverCards();
    return (
      <SectionCard theme={currentTheme} title="KPI Driver Scorecard">
        <div style={s.storyGrid}>
          {cards.map((card) => {
            const status = getKpiStatus(card.title, card.value);
            return (
              <div key={card.title} style={{ ...s.storyCard, border: `1px solid ${status.color}` }}>
                <div style={s.storyTitle}>{card.title}</div>
                <div style={{ ...s.kpiValue, marginBottom: 8, color: status.color }}>{card.value}</div>
                <div style={{ display: "inline-block", padding: "4px 8px", borderRadius: 999, background: status.bg, color: status.color, fontSize: 11, fontWeight: 900, marginBottom: 8 }}>{status.label}</div>
                <div style={s.storyText}>{card.note}</div>
              </div>
            );
          })}
        </div>
      </SectionCard>
    );
  };

  const getExecutiveExtraCharts = () => {
    if (dashboardType === "hr" && hrData) {
      return [
        {
          kind: "doughnut",
          title: "Workforce Distribution",
          subtitle: "Shows how employees are spread across work modes/status groups.",
          data: { labels: Object.keys(hrData.employeeDistributionMap || {}), datasets: [{ label: "Employees", data: Object.values(hrData.employeeDistributionMap || {}), backgroundColor: COLORS }] },
        },
        {
          kind: "bar",
          title: "Active vs Exited Employees",
          subtitle: "Quick health check for retention and workforce stability.",
          data: { labels: ["Active", "Exited"], datasets: [{ label: "Employees", data: [hrData.activeEmployees || 0, hrData.exitedEmployees || 0], backgroundColor: [COLORS[2], COLORS[4]], borderRadius: 14 }] },
        },
        {
          kind: "bar",
          title: "Avg Salary by Experience",
          subtitle: "Explains compensation movement across seniority levels.",
          data: { labels: Object.keys(hrData.salaryByExperienceMap || {}), datasets: [{ label: "Avg Salary", data: Object.values(hrData.salaryByExperienceMap || {}), backgroundColor: COLORS[3], borderRadius: 14 }] },
        },
        {
          kind: "line",
          title: "Hiring Salary Trend",
          subtitle: "Highlights month-wise salary movement for planning.",
          data: { labels: Object.keys(hrData.salaryTrendMap || {}).sort(), datasets: [{ label: "Salary Trend", data: Object.keys(hrData.salaryTrendMap || {}).sort().map((k) => hrData.salaryTrendMap[k]), borderColor: COLORS[5], backgroundColor: COLORS[5], pointRadius: 4, pointHoverRadius: 7, tension: 0.4 }] },
        },
      ];
    }
    if (dashboardType === "sales" && salesData) {
      return [
        {
          kind: "doughnut",
          title: "Sales by Channel",
          subtitle: "Shows which channel is carrying the revenue mix.",
          data: { labels: Object.keys(salesData.salesByChannelMap || {}), datasets: [{ label: "Revenue", data: Object.values(salesData.salesByChannelMap || {}), backgroundColor: COLORS }] },
        },
        {
          kind: "bar",
          title: "Revenue vs Profit",
          subtitle: "Separates top-line growth from real business earnings.",
          data: { labels: ["Revenue", "Profit"], datasets: [{ label: "Amount", data: [salesData.totalRevenue || 0, salesData.totalProfit || 0], backgroundColor: [COLORS[0], COLORS[2]], borderRadius: 14 }] },
        },
        {
          kind: "line",
          title: "Monthly Revenue Trend",
          subtitle: "Shows whether sales momentum is improving or slowing.",
          data: { labels: Object.keys(salesData.revenueTrendMap || {}).sort(), datasets: [{ label: "Revenue", data: Object.keys(salesData.revenueTrendMap || {}).sort().map((k) => salesData.revenueTrendMap[k]), borderColor: COLORS[2], backgroundColor: COLORS[2], pointRadius: 4, pointHoverRadius: 7, tension: 0.4 }] },
        },
        {
          kind: "bar",
          title: "Top Products by Revenue",
          subtitle: "Identifies the products that should receive sales focus.",
          data: { labels: topEntries(salesData.topProductMap, 8).map(([k]) => k), datasets: [{ label: "Revenue", data: topEntries(salesData.topProductMap, 8).map(([, v]) => v), backgroundColor: COLORS[1], borderRadius: 14 }] },
        },
      ];
    }
    if (dashboardType === "finance" && financeData) {
      return [
        {
          kind: "doughnut",
          title: "Payment Status Split",
          subtitle: "Shows collection quality and cash-flow pressure.",
          data: { labels: Object.keys(financeData.paymentStatusMap || {}), datasets: [{ label: "Transactions", data: Object.values(financeData.paymentStatusMap || {}), backgroundColor: COLORS }] },
        },
        {
          kind: "bar",
          title: "Revenue vs Expense vs Profit",
          subtitle: "Explains overall financial health in one view.",
          data: { labels: ["Revenue", "Expense", "Profit"], datasets: [{ label: "Amount", data: [financeData.totalRevenue || 0, financeData.totalExpense || 0, financeData.netProfit || 0], backgroundColor: [COLORS[0], COLORS[4], COLORS[2]], borderRadius: 14 }] },
        },
        {
          kind: "bar",
          title: "Budget vs Actual",
          subtitle: "Shows if execution is staying within planned limits.",
          data: { labels: ["Budget", "Actual"], datasets: [{ label: "Amount", data: [financeData.totalBudget || 0, financeData.totalActual || 0], backgroundColor: [COLORS[0], COLORS[3]], borderRadius: 14 }] },
        },
        {
          kind: "line",
          title: "Actual Trend",
          subtitle: "Tracks actual spend/revenue movement over time.",
          data: { labels: Object.keys(financeData.actualTrendMap || {}).sort(), datasets: [{ label: "Actual", data: Object.keys(financeData.actualTrendMap || {}).sort().map((k) => financeData.actualTrendMap[k]), borderColor: COLORS[5], backgroundColor: COLORS[5], pointRadius: 4, pointHoverRadius: 7, tension: 0.4 }] },
        },
      ];
    }
    if (dashboardType === "marketing" && marketingData) {
      return [
        {
          kind: "doughnut",
          title: "Revenue by Source",
          subtitle: "Shows where your strongest demand is coming from.",
          data: { labels: Object.keys(marketingData.revenueBySourceMap || {}), datasets: [{ label: "Revenue", data: Object.values(marketingData.revenueBySourceMap || {}), backgroundColor: COLORS }] },
        },
        {
          kind: "bar",
          title: "Spend vs Revenue",
          subtitle: "Compares investment against generated business value.",
          data: { labels: ["Spend", "Revenue"], datasets: [{ label: "Amount", data: [marketingData.totalSpend || 0, marketingData.totalRevenue || 0], backgroundColor: [COLORS[4], COLORS[2]], borderRadius: 14 }] },
        },
        {
          kind: "bar",
          title: "ROI by Channel",
          subtitle: "Finds which channel deserves budget scale-up.",
          data: { labels: topEntries(marketingData.roiByChannelMap, 8).map(([k]) => k), datasets: [{ label: "ROI %", data: topEntries(marketingData.roiByChannelMap, 8).map(([, v]) => v), backgroundColor: COLORS[2], borderRadius: 14 }] },
        },
        {
          kind: "bar",
          title: "Revenue per Campaign",
          subtitle: "Reveals the campaigns creating the biggest commercial impact.",
          data: { labels: topEntries(marketingData.revenueByCampaignMap, 8).map(([k]) => k), datasets: [{ label: "Revenue", data: topEntries(marketingData.revenueByCampaignMap, 8).map(([, v]) => v), backgroundColor: COLORS[1], borderRadius: 14 }] },
        },
      ];
    }
    return [];
  };

  const renderExecutiveChart = (chart) => {
    if (!chart) return null;
    if (chart.kind === "doughnut") return <Doughnut data={chart.data} options={pieOptions} compact />;
    if (chart.kind === "line") return <Line data={chart.data} options={lineOptions} compact />;
    return <Bar data={chart.data} options={commonChartOptions} compact />;
  };

  const renderStorySection = () => {
    const cards = getStoryCards();
    const aiCards = getAIInsightCards();
    const bullets = getDynamicExecutiveBullets();
    return (
      <>
        <SectionCard theme={currentTheme} title="Executive Story: Dynamic Decision Brief">
          <div style={{ display: "grid", gap: 10 }}>
            {bullets.map((item, idx) => (
              <div key={`${item.level}-${idx}`} style={bulletStyle(item.level)}>
                <span style={{ fontWeight: 900 }}>
                  {item.level === "risk" ? "🔴" : item.level === "warning" ? "🟡" : item.level === "good" ? "🟢" : item.level === "action" ? "👉" : "📌"}
                </span>
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard theme={currentTheme} title="Executive Summary Details">
          <div style={s.storyGrid}>
            {cards.map((card) => (
              <div key={card.title} style={s.storyCard}>
                <div style={s.storyTitle}>{card.title}</div>
                <div style={s.storyText}>{card.text}</div>
              </div>
            ))}
          </div>
        </SectionCard>

        {aiCards.length > 0 ? (
          <SectionCard theme={currentTheme} title="AI Insights & Recommended Actions">
            <div style={s.storyGrid}>
              {aiCards.map((card) => (
                <div key={card.title} style={s.storyCard}>
                  <div style={s.storyTitle}>{card.title}</div>
                  <div style={s.storyText}>{card.text}</div>
                </div>
              ))}
            </div>
          </SectionCard>
        ) : null}

        {getExecutiveExtraCharts().length > 0 ? (
          <SectionCard theme={currentTheme} title="Executive Story Visuals: 4 Decision Charts">
            <div style={s.executiveVisualGrid}>
              {getExecutiveExtraCharts().map((chart) => (
                <ChartCard key={chart.title} theme={currentTheme} title={chart.title} subtitle={chart.subtitle} compact>
                  {renderExecutiveChart(chart)}
                </ChartCard>
              ))}
            </div>
          </SectionCard>
        ) : null}
      </>
    );
  };

  const renderVisibleTabSection = () => {
    if (activeTab === "builder") {
      return renderBuilderSection();
    }

    if (dashboardType === "hr" && hrData) {
      if (activeTab === "story") {
        return (
          <div style={s.sectionStack}>
            {renderStorySection()}
          </div>
        );
      }
      return (
        <div style={s.sectionStack}>
          {renderKpiDriverSection()}
          <div style={s.chartGrid}>
            <ChartCard theme={currentTheme} title="Salary by Experience Level"><Bar data={{ labels: Object.keys(hrData.salaryByExperienceMap), datasets: [{ label: "Avg Salary", data: Object.values(hrData.salaryByExperienceMap), backgroundColor: COLORS[3], borderRadius: 8 }] }} options={commonChartOptions} /></ChartCard>
            <ChartCard theme={currentTheme} title="Applicants per Work Mode"><Bar data={{ labels: Object.keys(hrData.applicantsPerWorkModeMap), datasets: [{ label: "Applicants", data: Object.values(hrData.applicantsPerWorkModeMap), backgroundColor: COLORS[1], borderRadius: 8 }] }} options={commonChartOptions} /></ChartCard>
            <ChartCard theme={currentTheme} title="Hiring Conversion by Department"><Bar data={{ labels: topEntries(hrData.hiringConversionByDepartmentMap, 8).map(([k]) => k), datasets: [{ label: "Conversion %", data: topEntries(hrData.hiringConversionByDepartmentMap, 8).map(([, v]) => v), backgroundColor: COLORS[2], borderRadius: 8 }] }} options={commonChartOptions} /></ChartCard>
            <ChartCard theme={currentTheme} title="Hiring Salary Trend"><Line data={{ labels: Object.keys(hrData.salaryTrendMap).sort(), datasets: [{ label: "Salary Trend", data: Object.keys(hrData.salaryTrendMap).sort().map((k) => hrData.salaryTrendMap[k]), borderColor: COLORS[5], backgroundColor: COLORS[5], tension: 0.35 }] }} options={lineOptions} /></ChartCard>
          </div>
        </div>
      );
    }

    if (dashboardType === "sales" && salesData) {
      if (activeTab === "story") {
        return (
          <div style={s.sectionStack}>
            {renderStorySection()}
          </div>
        );
      }
      return (
        <div style={s.sectionStack}>
          {renderKpiDriverSection()}
          <div style={s.chartGrid}>
            <ChartCard theme={currentTheme} title="Top Products by Revenue"><Bar data={{ labels: topEntries(salesData.topProductMap, 8).map(([k]) => k), datasets: [{ label: "Revenue", data: topEntries(salesData.topProductMap, 8).map(([, v]) => v), backgroundColor: COLORS[0], borderRadius: 8 }] }} options={commonChartOptions} /></ChartCard>
            <ChartCard theme={currentTheme} title="Sales rep performance"><Bar data={{ labels: topEntries(salesData.revenueByRepMap, 8).map(([k]) => k), datasets: [{ label: "Revenue", data: topEntries(salesData.revenueByRepMap, 8).map(([, v]) => v), backgroundColor: COLORS[6], borderRadius: 8 }] }} options={commonChartOptions} /></ChartCard>
            <ChartCard theme={currentTheme} title="Profit per Order by Region"><Bar data={{ labels: topEntries(salesData.profitPerOrderByRegionMap, 8).map(([k]) => k), datasets: [{ label: "Profit / Order", data: topEntries(salesData.profitPerOrderByRegionMap, 8).map(([, v]) => v), backgroundColor: COLORS[2], borderRadius: 8 }] }} options={commonChartOptions} /></ChartCard>
            <ChartCard theme={currentTheme} title="Sales by Channel"><Doughnut data={{ labels: Object.keys(salesData.salesByChannelMap), datasets: [{ label: "Revenue", data: Object.values(salesData.salesByChannelMap), backgroundColor: COLORS }] }} options={pieOptions} /></ChartCard>
          </div>
        </div>
      );
    }

    if (dashboardType === "finance" && financeData) {
      if (activeTab === "story") {
        return (
          <div style={s.sectionStack}>
            {renderStorySection()}
          </div>
        );
      }
      return (
        <div style={s.sectionStack}>
          {renderKpiDriverSection()}
          <div style={s.chartGrid}>
            <ChartCard theme={currentTheme} title="Variance by Department"><Bar data={{ labels: topEntries(financeData.varianceByDepartmentMap, 8).map(([k]) => k), datasets: [{ label: "Variance", data: topEntries(financeData.varianceByDepartmentMap, 8).map(([, v]) => v), backgroundColor: COLORS[4], borderRadius: 8 }] }} options={commonChartOptions} /></ChartCard>
            <ChartCard theme={currentTheme} title="Payment Realization"><Doughnut data={{ labels: Object.keys(financeData.paymentStatusMap), datasets: [{ label: "Transactions", data: Object.values(financeData.paymentStatusMap), backgroundColor: COLORS }] }} options={pieOptions} /></ChartCard>
            <ChartCard theme={currentTheme} title="Expense by Category"><Bar data={{ labels: topEntries(financeData.expenseByCategoryMap, 8).map(([k]) => k), datasets: [{ label: "Expense", data: topEntries(financeData.expenseByCategoryMap, 8).map(([, v]) => v), backgroundColor: COLORS[7], borderRadius: 8 }] }} options={commonChartOptions} /></ChartCard>
            <ChartCard theme={currentTheme} title="Actual Trend"><Line data={{ labels: Object.keys(financeData.actualTrendMap).sort(), datasets: [{ label: "Actual", data: Object.keys(financeData.actualTrendMap).sort().map((k) => financeData.actualTrendMap[k]), borderColor: COLORS[5], backgroundColor: COLORS[5], tension: 0.35 }] }} options={lineOptions} /></ChartCard>
          </div>
        </div>
      );
    }

    if (dashboardType === "marketing" && marketingData) {
      if (activeTab === "story") {
        return (
          <div style={s.sectionStack}>
            {renderStorySection()}
          </div>
        );
      }
      return (
        <div style={s.sectionStack}>
          {renderKpiDriverSection()}
          <div style={s.chartGrid}>
            <ChartCard theme={currentTheme} title="CTR by Source"><Bar data={{ labels: topEntries(marketingData.ctrBySourceMap, 8).map(([k]) => k), datasets: [{ label: "CTR %", data: topEntries(marketingData.ctrBySourceMap, 8).map(([, v]) => v), backgroundColor: COLORS[0], borderRadius: 8 }] }} options={commonChartOptions} /></ChartCard>
            <ChartCard theme={currentTheme} title="Conversion Rate by Source"><Bar data={{ labels: topEntries(marketingData.conversionRateBySourceMap, 8).map(([k]) => k), datasets: [{ label: "Conversion %", data: topEntries(marketingData.conversionRateBySourceMap, 8).map(([, v]) => v), backgroundColor: COLORS[3], borderRadius: 8 }] }} options={commonChartOptions} /></ChartCard>
            <ChartCard theme={currentTheme} title="Cost per Lead by Source"><Bar data={{ labels: topEntries(marketingData.costPerLeadBySourceMap, 8).map(([k]) => k), datasets: [{ label: "CPL", data: topEntries(marketingData.costPerLeadBySourceMap, 8).map(([, v]) => v), backgroundColor: COLORS[4], borderRadius: 8 }] }} options={commonChartOptions} /></ChartCard>
            <ChartCard theme={currentTheme} title="Click to Conversion by Campaign"><Bar data={{ labels: topEntries(marketingData.clickToConversionByCampaignMap, 8).map(([k]) => k), datasets: [{ label: "Conversion / Click %", data: topEntries(marketingData.clickToConversionByCampaignMap, 8).map(([, v]) => v), backgroundColor: COLORS[6], borderRadius: 8 }] }} options={commonChartOptions} /></ChartCard>
          </div>
        </div>
      );
    }

    return null;
  };


  const renderBuilderSection = () => {
    const effectiveDashboard = builderDashboardType === "current" ? dashboardType : builderDashboardType;
    const totalRevenue = effectiveDashboard === "sales" ? salesData?.totalRevenue : effectiveDashboard === "finance" ? financeData?.totalRevenue : effectiveDashboard === "marketing" ? marketingData?.totalRevenue : effectiveDashboard === "connected" ? calculateConnectedKpis().totalRevenue : 0;
    const totalProfit = effectiveDashboard === "sales" ? salesData?.totalProfit : effectiveDashboard === "finance" ? financeData?.netProfit : effectiveDashboard === "connected" ? calculateConnectedKpis().totalProfit : 0;
    const marginValue = effectiveDashboard === "sales" ? salesData?.profitMargin : effectiveDashboard === "finance" ? financeData?.profitMargin : effectiveDashboard === "marketing" ? marketingData?.roi : effectiveDashboard === "connected" ? calculateConnectedKpis().margin : 0;

    const generateBuilderInsight = () => {
      const alerts = generateSmartAlerts().map((item) => item.text);
      const sourceName = effectiveDashboard === "current" ? dashboardName : `${String(effectiveDashboard || "dashboard").toUpperCase()} dashboard`;
      const insight = [
        `${sourceName}: custom layout has ${builderWidgets.length} widgets selected.`,
        totalRevenue ? `Revenue focus is ${formatCurrency(totalRevenue)}.` : "Revenue KPI is not available for this selected dashboard.",
        totalProfit ? `Profit focus is ${formatCurrency(totalProfit)} with ${formatPercent(marginValue)} margin/ROI indicator.` : `Current margin/ROI indicator is ${formatPercent(marginValue)}.`,
        alerts[0] || "No major risk detected from the selected view.",
        "Recommended action: keep top KPIs first, then place trend/segment charts, then close with insight card for client decision-making.",
      ].join(" ");
      setBuilderGeneratedInsight(insight);
    };

    const renderBuilderChart = (widgetType) => {
      const chartLabels = filteredRows.slice(0, 8).map((row, index) => row.Campaign_Name || row.Product_Name || row.Region || row.Department || row.Category || `Row ${index + 1}`);
      const chartValues = filteredRows.slice(0, 8).map((row) => Number(row.Revenue || row.Profit || row.Salary || row.Applicants_Count || row.Conversions || 0));
      const chartData = { labels: chartLabels, datasets: [{ label: "Selected Metric", data: chartValues, backgroundColor: COLORS, borderColor: COLORS[0], borderRadius: 8, tension: 0.35 }] };
      if (widgetType === "line") return <Line data={chartData} options={lineOptions} />;
      if (widgetType === "pie") return <Doughnut data={chartData} options={pieOptions} />;
      return <Bar data={chartData} options={commonChartOptions} />;
    };

    return (
      <div style={s.sectionStack}>
        <SectionCard theme={currentTheme} title="Custom Dashboard Builder">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14, alignItems: "end", marginBottom: 16 }}>
            <div>
              <div style={s.storyTitle}>Select Dashboard Type</div>
              <select style={s.input} value={builderDashboardType} onChange={(e) => setBuilderDashboardType(e.target.value)}>
                <option value="current">Current Uploaded Dashboard</option>
                <option value="sales">Sales Dashboard</option>
                <option value="finance">Finance Dashboard</option>
                <option value="marketing">Marketing Dashboard</option>
                <option value="hr">HR Dashboard</option>
                <option value="connected">Connected Data Dashboard</option>
              </select>
            </div>
            <button style={{ ...s.primaryButton, width: "auto" }} onClick={generateBuilderInsight}>Generate Insight From Dashboard</button>
            <button style={{ ...s.secondaryButton, width: "auto" }} onClick={saveBuilderLayout} disabled={builderSaving}>{builderSaving ? "Saving..." : "Save Layout"}</button>
          </div>

          <p style={{ marginTop: 0, color: currentTheme.muted, lineHeight: 1.6 }}>Design the client dashboard yourself: add KPI cards, bar/line/pie charts, insight cards, reorder them, then generate an executive insight from the completed layout.</p>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
            <button style={{ ...s.secondaryButton, width: "auto" }} onClick={() => addBuilderWidget("revenue")}>Add Revenue KPI</button>
            <button style={{ ...s.secondaryButton, width: "auto" }} onClick={() => addBuilderWidget("profit")}>Add Profit KPI</button>
            <button style={{ ...s.secondaryButton, width: "auto" }} onClick={() => addBuilderWidget("margin")}>Add Margin/ROI KPI</button>
            <button style={{ ...s.secondaryButton, width: "auto" }} onClick={() => addBuilderWidget("chart")}>Add Bar Chart</button>
            <button style={{ ...s.secondaryButton, width: "auto" }} onClick={() => addBuilderWidget("line")}>Add Line Chart</button>
            <button style={{ ...s.secondaryButton, width: "auto" }} onClick={() => addBuilderWidget("pie")}>Add Pie Chart</button>
            <button style={{ ...s.secondaryButton, width: "auto" }} onClick={() => addBuilderWidget("insight")}>Add Insight Card</button>
          </div>

          {builderGeneratedInsight ? (
            <div style={{ ...s.storyCard, marginBottom: 16, borderColor: currentTheme.accent }}>
              <div style={s.storyTitle}>Generated Dashboard Insight</div>
              <div style={s.storyText}>{builderGeneratedInsight}</div>
            </div>
          ) : null}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
            {builderWidgets.map((widget, index) => {
              const content = widget.type === "revenue" ? formatCurrency(totalRevenue) : widget.type === "profit" ? formatCurrency(totalProfit) : widget.type === "margin" ? formatPercent(marginValue) : widget.type === "insight" ? (builderGeneratedInsight || "Generate insight after finalizing the layout.") : null;
              const isChartWidget = ["chart", "line", "pie"].includes(widget.type);
              return (
                <div
                  key={widget.id}
                  draggable
                  onDragStart={() => setDraggingBuilderId(widget.id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    const fromIndex = builderWidgets.findIndex((item) => item.id === draggingBuilderId);
                    if (fromIndex >= 0) moveBuilderWidget(fromIndex, index);
                    setDraggingBuilderId(null);
                  }}
                  style={{ ...s.storyCard, cursor: "grab", minHeight: isChartWidget ? 390 : 150, display: "flex", flexDirection: "column", justifyContent: "space-between" }}
                >
                  <div>
                    <div style={s.storyTitle}>☰ {widget.title}</div>
                    {isChartWidget ? <div style={{ height: 310 }}>{renderBuilderChart(widget.type)}</div> : <div style={{ ...s.kpiValue, fontSize: widget.type === "insight" ? 16 : 24 }}>{content}</div>}
                    <div style={s.storyText}>Selected: {effectiveDashboard || "current"}. Use drag/drop or arrow buttons to arrange this widget.</div>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                    <button style={{ ...s.secondaryButton, width: "auto", padding: "6px 10px" }} onClick={() => moveBuilderWidget(index, index - 1)}>↑</button>
                    <button style={{ ...s.secondaryButton, width: "auto", padding: "6px 10px" }} onClick={() => moveBuilderWidget(index, index + 1)}>↓</button>
                    <button style={{ ...s.secondaryButton, width: "auto", padding: "6px 10px" }} onClick={() => removeBuilderWidget(widget.id)}>Remove</button>
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>
      </div>
    );
  };

  const renderConnectedDataSection = () => {
    const renderConnectControls = () => (
      <SectionCard theme={currentTheme} title="Connect Data Page">
        <p style={{ marginTop: 0, color: currentTheme.muted, lineHeight: 1.6 }}>
          Connect CRM, ERP, SQL Server API, live JSON endpoint, API secret key, or a SQL/CSV/Excel exported data file. Bivora will map only required KPI fields and safely ignore extra columns.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14 }}>
          <div style={s.storyCard}>
            <div style={s.storyTitle}>API / CRM / ERP / SQL Server Endpoint</div>
            <input style={s.input} value={apiUrl} onChange={(e) => setApiUrl(e.target.value)} placeholder="https://your-crm-or-sql-api.com/data" />
            <select style={s.input} value={apiAuthType} onChange={(e) => setApiAuthType(e.target.value)}>
              <option value="bearer">Bearer Token</option>
              <option value="x-api-key">X-API-Key</option>
              <option value="query">Query key (?api_key=)</option>
              <option value="none">No key</option>
            </select>
            <input style={s.input} value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="API secret key / token (optional)" type="password" />
            <button style={s.primaryButton} onClick={loadApiData} disabled={apiLoading}>{apiLoading ? "Connecting..." : "Connect Live Data"}</button>
          </div>
          <div style={s.storyCard}>
            <div style={s.storyTitle}>SQL File / Exported Data File</div>
            <p style={{ color: currentTheme.muted, fontSize: 13, lineHeight: 1.5 }}>Upload CSV, XLS, or XLSX exported from SQL Server, CRM, ERP, or BI tools.</p>
            <input style={s.input} type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setSqlFile(e.target.files?.[0] || null)} />
            <button style={s.primaryButton} onClick={loadSqlFileData} disabled={apiLoading}>{apiLoading ? "Reading..." : "Connect File Data"}</button>
          </div>
        </div>
      </SectionCard>
    );

    if (!connectedRows.length && !connectedRawRows.length) {
      return renderConnectControls();
    }

    const connectedKpis = calculateConnectedKpis();
    const connectedStory = [
      ["Connected Source", connectedSourceName || "Live API / JSON source"],
      ["Data Volume", `${formatNumber(connectedKpis.rows)} mapped rows are being used for KPI calculation. Extra API columns are ignored.`],
      ["Revenue Health", `Connected revenue is ${formatCurrency(connectedKpis.totalRevenue)} with ${formatPercent(connectedKpis.margin)} margin.`],
      ["Decision Insight", connectedKpis.margin < 10 ? "Margin is weak. Review cost, pricing, and low-profit segments." : "Margin looks healthy. Focus on scaling the strongest segment carefully."],
    ];

    const mappingRows = [
      ["Revenue", "Used for revenue KPIs and trend charts"],
      ["Profit", "Used for margin and profitability KPIs"],
      ["Cost", "Used when profit is missing and for cost control"],
      ["Region", "Used for regional performance"],
      ["Department", "Used for department performance"],
      ["Category", "Used for segment/category insights"],
      ["Date", "Used for trend and forecast readiness"],
    ];

    return (
      <div style={s.sectionStack}>
        {renderConnectControls()}
        <SectionCard theme={currentTheme} title="Connected Data Field Mapping">
          <p style={{ marginTop: 0, color: currentTheme.muted, lineHeight: 1.6 }}>
            Auto mapping picks only the fields needed for Bivora KPIs. Other CRM/ERP/SQL columns are ignored safely. You can manually change the mapping below.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 12 }}>
            {mappingRows.map(([target, help]) => (
              <div key={target} style={{ padding: 12, borderRadius: 14, background: currentTheme.secondaryBg, border: `1px solid ${currentTheme.border}` }}>
                <div style={{ fontWeight: 900, marginBottom: 6 }}>{target}</div>
                <select
                  style={{ ...s.input, marginBottom: 6 }}
                  value={connectedFieldMapping[target] || ""}
                  onChange={(e) => {
                    const next = { ...connectedFieldMapping, [target]: e.target.value };
                    setConnectedFieldMapping(next);
                    applyConnectedMapping(connectedRawRows, next);
                  }}
                >
                  <option value="">Not mapped</option>
                  {connectedFieldOptions.map((field) => (
                    <option key={field} value={field}>{field}</option>
                  ))}
                </select>
                <div style={{ color: currentTheme.muted, fontSize: 12, lineHeight: 1.4 }}>{help}</div>
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard theme={currentTheme} title="Connected Data KPI Overview">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
            <MiniKpi theme={currentTheme} title="Connected Rows" value={formatNumber(connectedKpis.rows)} />
            <MiniKpi theme={currentTheme} title="Connected Revenue" value={formatCurrency(connectedKpis.totalRevenue)} />
            <MiniKpi theme={currentTheme} title="Connected Profit" value={formatCurrency(connectedKpis.totalProfit)} />
            <MiniKpi theme={currentTheme} title="Connected Margin" value={formatPercent(connectedKpis.margin)} />
          </div>
        </SectionCard>

        <SectionCard theme={currentTheme} title="Connected Data Executive Story">
          <div style={s.storyGrid}>
            {connectedStory.map(([title, text]) => (
              <div key={title} style={s.storyCard}>
                <div style={s.storyTitle}>{title}</div>
                <div style={s.storyText}>{text}</div>
              </div>
            ))}
          </div>
        </SectionCard>

        <div style={s.chartGrid}>
          <ChartCard theme={currentTheme} title="Connected Revenue by Region">
            <Bar data={{ labels: topEntries(groupBySum(connectedRows, "Region", "Revenue"), 8).map(([k]) => k), datasets: [{ label: "Revenue", data: topEntries(groupBySum(connectedRows, "Region", "Revenue"), 8).map(([, v]) => v), backgroundColor: COLORS[1], borderRadius: 8 }] }} options={commonChartOptions} />
          </ChartCard>
          <ChartCard theme={currentTheme} title="Connected Profit by Category">
            <Bar data={{ labels: topEntries(groupBySum(connectedRows, "Category", "Profit"), 8).map(([k]) => k), datasets: [{ label: "Profit", data: topEntries(groupBySum(connectedRows, "Category", "Profit"), 8).map(([, v]) => v), backgroundColor: COLORS[2], borderRadius: 8 }] }} options={commonChartOptions} />
          </ChartCard>
        </div>
      </div>
    );
  };

  const renderAdvancedFeaturePanel = () => {
    if (!dashboardType) return null;
    const alerts = generateSmartAlerts();
    const forecast = forecastNextRevenue();
    const comparison = compareSummary();
    const comparisonInsights = getComparisonInsights();
    const regionOptions = optionSet.Region || [];
    const departmentOptions = optionSet.Department || [];
    const sourceOptions = optionSet.Source || [];
    const quickOptions = regionOptions.length ? { key: "Region", values: regionOptions } : departmentOptions.length ? { key: "Department", values: departmentOptions } : sourceOptions.length ? { key: "Source", values: sourceOptions } : null;
    const compactCard = { ...s.panelCard, minHeight: 250, marginBottom: 0, display: "flex", flexDirection: "column", justifyContent: "space-between" };
    const fourGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16, alignItems: "stretch" };

    return (
      <div style={{ display: "grid", gap: 18, marginBottom: 20 }}>
        <div style={{ ...s.panelCard, border: `1px solid ${currentTheme.border}`, marginBottom: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div>
              <h3 style={s.panelTitle}>Client Intelligence Center</h3>
              <p style={{ margin: "4px 0 0", color: currentTheme.muted, fontSize: 13, lineHeight: 1.6 }}>
                Export, AI Data Chat, alerts, drill-down, forecast, custom builder, and file comparison are organized for client-ready analysis.
              </p>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button style={{ ...s.secondaryButton, width: "auto" }} onClick={exportPDF}>Download Client PDF</button>
              <button style={{ ...s.secondaryButton, width: "auto" }} onClick={() => setActiveTab("connected")}>Open Connect Data Page</button>
            </div>
          </div>
        </div>

        <SectionCard theme={currentTheme} title="AI Forecast">
          <p style={{ marginTop: 0, color: currentTheme.muted, lineHeight: 1.6 }}>{forecast.note}</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 14 }}>
            <MiniKpi theme={currentTheme} title="Previous Period" value={formatCurrency(forecast.previous)} />
            <MiniKpi theme={currentTheme} title="Current Period" value={formatCurrency(forecast.current)} />
            <MiniKpi theme={currentTheme} title="Forecast" value={formatCurrency(forecast.nextValue)} />
            <MiniKpi theme={currentTheme} title="Projected Change" value={`${forecast.changeAmount >= 0 ? "+" : ""}${formatCurrency(forecast.changeAmount)}`} status={forecast.changeAmount >= 0 ? "good" : "risk"} />
          </div>
          <div style={{ overflowX: "auto", border: `1px solid ${currentTheme.border}`, borderRadius: 16 }}>
            <table style={{ width: "100%", minWidth: 720, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: currentTheme.secondaryBg }}>
                  {["Previous", "Current", "Forecast", "Change", "Confidence", "Limit"].map((h) => (
                    <th key={h} style={{ padding: "12px 14px", textAlign: "left", color: currentTheme.muted, fontSize: 12, fontWeight: 900 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: "14px", color: currentTheme.text, fontWeight: 900 }}>{formatCurrency(forecast.previous)}</td>
                  <td style={{ padding: "14px", color: currentTheme.text, fontWeight: 900 }}>{formatCurrency(forecast.current)}</td>
                  <td style={{ padding: "14px", color: currentTheme.text, fontWeight: 900 }}>{formatCurrency(forecast.nextValue)}</td>
                  <td style={{ padding: "14px", color: forecast.changeAmount >= 0 ? "#10b981" : "#ef4444", fontWeight: 900 }}>{forecast.changeAmount >= 0 ? "+" : ""}{formatCurrency(forecast.changeAmount)} ({formatPercent(forecast.cappedPct)})</td>
                  <td style={{ padding: "14px", color: currentTheme.text, fontWeight: 900 }}>{forecast.confidence}</td>
                  <td style={{ padding: "14px", color: currentTheme.muted, fontWeight: 800 }}>±30%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </SectionCard>

        <div style={fourGrid}>
          <div style={compactCard}>
            <div>
              <h3 style={s.panelTitle}>Smart Alerts</h3>
              <div style={{ display: "grid", gap: 10 }}>
                {alerts.map((alert, index) => {
                  const color = alert.type === "risk" ? "#ef4444" : alert.type === "warning" ? "#f59e0b" : alert.type === "success" ? "#10b981" : currentTheme.accent;
                  return <div key={index} style={{ padding: 12, borderRadius: 14, border: `1px solid ${color}`, color, background: `${color}18`, fontWeight: 800, fontSize: 13, lineHeight: 1.5 }}>{alert.text}</div>;
                })}
              </div>
            </div>
          </div>

          <div style={compactCard}>
            <div>
              <h3 style={s.panelTitle}>Drill Down Analytics</h3>
              <label style={{ display: "flex", gap: 8, alignItems: "center", color: currentTheme.muted, fontSize: 13, marginBottom: 12 }}>
                <input type="checkbox" checked={drillDownEnabled} onChange={(e) => setDrillDownEnabled(e.target.checked)} /> Enable chart click filters
              </label>
              {quickOptions ? <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>{quickOptions.values.slice(0, 8).map((value) => <button key={value} style={{ ...s.secondaryButton, width: "auto" }} onClick={() => setFilters((prev) => ({ ...prev, [quickOptions.key]: value }))}>{value}</button>)}<button style={{ ...s.secondaryButton, width: "auto" }} onClick={() => setFilters({})}>Clear</button></div> : <p style={{ color: currentTheme.muted }}>No drill-down fields found.</p>}
            </div>
          </div>

          <div style={compactCard}>
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                <h3 style={s.panelTitle}>AI Data Chat</h3>
                {aiChatMessages.length > 0 ? <button style={{ ...s.secondaryButton, width: "auto", padding: "7px 10px" }} onClick={() => setAiChatMessages([])}>Clear Chat</button> : null}
              </div>
              <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                <input style={{ ...s.input, marginBottom: 0 }} value={aiQuestion} onChange={(e) => setAiQuestion(e.target.value)} placeholder="Ask about profit, region, employee..." onKeyDown={(e) => { if (e.key === "Enter") answerAIQuestion(); }} />
                <button style={{ ...s.primaryButton, width: 90 }} onClick={answerAIQuestion}>Ask</button>
              </div>
              <div style={{ display: "grid", gap: 10, maxHeight: 160, overflow: "auto" }}>
                {aiChatMessages.length === 0 ? <p style={{ color: currentTheme.muted, lineHeight: 1.5 }}>Ask a question about this dashboard.</p> : null}
                {aiChatMessages.map((item, index) => <div key={index} style={{ padding: 12, borderRadius: 14, background: currentTheme.secondaryBg, border: `1px solid ${currentTheme.border}` }}><div style={{ fontWeight: 900, marginBottom: 6 }}>Q: {item.question}</div><div style={{ color: currentTheme.muted, lineHeight: 1.5 }}>{item.answer}</div><button style={{ ...s.secondaryButton, width: "auto", padding: "5px 9px", marginTop: 8 }} onClick={() => setAiChatMessages((prev) => prev.filter((_, i) => i !== index))}>Delete</button></div>)}
              </div>
            </div>
          </div>

          <div style={compactCard}>
            <div>
              <h3 style={s.panelTitle}>Custom Dashboard Builder</h3>
              <p style={{ color: currentTheme.muted, fontSize: 13, lineHeight: 1.5 }}>Choose what should be emphasized in client view.</p>
              <div style={{ display: "grid", gap: 9 }}>
                {[["revenue", "Revenue KPI"], ["profit", "Profit KPI"], ["margin", "Margin KPI"], ["topPerformer", "Top performer insight"]].map(([key, label]) => <label key={key} style={{ display: "flex", gap: 8, alignItems: "center", color: currentTheme.text }}><input type="checkbox" checked={builderKpis.includes(key)} onChange={(e) => setBuilderKpis((prev) => e.target.checked ? [...new Set([...prev, key])] : prev.filter((item) => item !== key))} />{label}</label>)}
              </div>
            </div>
          </div>

          <div style={compactCard}>
            <div>
              <h3 style={s.panelTitle}>Multi-file Comparison</h3>
              <p style={{ color: currentTheme.muted, fontSize: 13, lineHeight: 1.5 }}>Upload two CSV or Excel files to compare revenue, profit, cost, and trend.</p>
              <input style={s.input} type="file" accept=".csv,.xlsx,.xls" onChange={(e) => handleCompareFile(e.target.files?.[0], "A")} />
              <input style={s.input} type="file" accept=".csv,.xlsx,.xls" onChange={(e) => handleCompareFile(e.target.files?.[0], "B")} />
              <div style={{ padding: 12, borderRadius: 14, background: currentTheme.secondaryBg, border: `1px solid ${currentTheme.border}`, display: "grid", gap: 6, fontSize: 13 }}>
                <div><strong>{compareFileAName || "File A"}:</strong> {formatCurrency(comparison.a)}</div>
                <div><strong>{compareFileBName || "File B"}:</strong> {formatCurrency(comparison.b)}</div>
                <div><strong>Revenue Change:</strong> {formatCurrency(comparison.diff)} ({formatPercent(comparison.pct)})</div>
              </div>
            </div>
          </div>

          <div style={compactCard}>
            <div>
              <h3 style={s.panelTitle}>Comparison Insights</h3>
              <div style={{ display: "grid", gap: 9 }}>
                {comparisonInsights.map((insight, index) => <div key={index} style={{ padding: 11, borderRadius: 13, background: currentTheme.secondaryBg, border: `1px solid ${currentTheme.border}`, color: currentTheme.text, fontSize: 13, lineHeight: 1.45 }}>{insight}</div>)}
              </div>
            </div>
          </div>

        </div>
      </div>
    );
  };

  const renderFullExportContent = () => {
    if (!dashboardType) return null;

    return (
      <div style={s.exportShell}>
        <div style={s.exportHeader}>
          <div>
            <div style={s.exportTitle}>Bivora Smart Analytics</div>
            <div style={s.exportSubtitle}>{dashboardName}</div>
            <div style={s.exportMeta}>Source: {sheetName}</div>
          </div>
        </div>

        {renderHeroKpis()}
        {renderStorySection()}
        {renderAdvancedFeaturePanel()}
        {renderVisibleTabSection()}
      </div>
    );
  };

  return (
    <div style={s.app}>
      <div style={s.container}>
        <DashboardStatusCard
          subscriptionInfo={subscriptionInfo}
          subscriptionLoading={subscriptionLoading}
          theme={theme}
        />

        <div style={s.hero}>
          <div style={s.heroLeft}>
            <div style={s.logoWrap}>
              <img
                src="/bivora-logo.png"
                alt="Bivora"
                style={s.logo}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
              <div>
                <h1 style={s.title}>Bivora Smart Analytics</h1>
                <p style={s.subtitle}>KPI storytelling dashboard for premium client pitches</p>
              </div>
            </div>
          </div>

          <div style={s.heroRight}>
            <button style={presentationMode ? s.primaryButton : s.secondaryButton} onClick={() => setPresentationMode((prev) => !prev)}>
              {presentationMode ? "Exit Presentation" : "Presentation Mode"}
            </button>
            {!presentationMode ? (
              <>
            <button style={s.demoButton} onClick={() => loadDemo("hr")}>HR Demo</button>
            <button style={s.demoButton} onClick={() => loadDemo("sales")}>Sales Demo</button>
            <button style={s.demoButton} onClick={() => loadDemo("finance")}>Finance Demo</button>
            <button style={s.demoButton} onClick={() => loadDemo("marketing")}>Marketing Demo</button>
            <button style={s.themeButton} onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
              {theme === "dark" ? "Light Mode" : "Dark Mode"}
            </button>
              </>
            ) : null}
          </div>
        </div>

        <UserStatusCard
          theme={theme}
          subscriptionLoading={subscriptionLoading}
          hasPaidAccess={hasPaidAccess}
          hasUploadAccess={hasUploadAccess}
          hasPremiumAccess={hasPremiumAccess}
          isAdminBypass={isAdminBypass}
          isInternalTeam={subscriptionInfo?.isInternalTeam}
          isAdmin={subscriptionInfo?.isAdmin}
          freeUploadsRemaining={freeUploadsRemaining}
          subscription={subscriptionInfo?.subscription}
        />

        {!subscriptionLoading && !hasPremiumAccess ? (
          <SubscriptionPlans
            onSubscriptionActivated={fetchSubscription}
            onPaymentSuccess={(details) => {
              setPaymentSuccess(details);
              fetchSubscription();
            }}
            theme={theme}
          />
        ) : null}

        <PaymentSuccessModal
          open={!!paymentSuccess}
          details={paymentSuccess}
          onClose={() => setPaymentSuccess(null)}
        />

        {!presentationMode ? (
        <div style={s.topPanel}>
          <ProtectedPaidFeature hasAccess={hasUploadAccess} title="Upload Locked">
            <div style={s.panelCard}>
              <h3 style={s.panelTitle}>Upload File</h3>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => setFile(e.target.files[0])}
                style={s.input}
              />
              <button style={s.primaryButton} onClick={uploadFile} disabled={loading}>
                {loading ? "Uploading..." : "Upload & Detect"}
              </button>
              {!hasPaidAccess && !isAdminBypass ? (
                <p style={{ marginTop: 10, fontSize: 12, color: currentTheme.muted }}>
                  Free trial uploads remaining: {freeUploadsRemaining}
                </p>
              ) : null}
            </div>
          </ProtectedPaidFeature>

          <div style={s.panelCard}>
            <h3 style={s.panelTitle}>Download Template</h3>
            <div style={s.buttonGroup}>
              <button style={s.secondaryButton} onClick={() => downloadTemplate("hr")}>HR Template</button>
              <button style={s.secondaryButton} onClick={() => downloadTemplate("sales")}>Sales Template</button>
              <button style={s.secondaryButton} onClick={() => downloadTemplate("finance")}>Finance Template</button>
              <button style={s.secondaryButton} onClick={() => downloadTemplate("marketing")}>Marketing Template</button>
            </div>
          </div>

          <ProtectedPaidFeature hasAccess={hasPremiumAccess} title="Export Locked">
            <div style={s.panelCard}>
              <h3 style={s.panelTitle}>Export Full Report</h3>
              <div style={s.buttonGroup}>
                <button style={s.secondaryButton} onClick={exportPDF}>Download Full PDF</button>
                <button style={s.secondaryButton} onClick={printFullReport}>Print Full Report</button>
                <button
                  style={s.secondaryButton}
                  onClick={() => {
                    setFilters({});
                    setSearchText("");
                  }}
                >
                  Reset Filters
                </button>
              </div>
            </div>
          </ProtectedPaidFeature>

          <div style={s.panelCard}>
            <h3 style={s.panelTitle}>Save Current Report</h3>
            <p style={{ marginTop: 0, color: currentTheme.muted, fontSize: 13 }}>
              Free users can save 1 report. Paid users, admins, and internal team can save more.
            </p>
            <button style={s.secondaryButton} onClick={saveCurrentReport}>
              Save Report
            </button>
          </div>
        </div>
        ) : null}

        {message && !presentationMode ? <div style={s.message}>{message}</div> : null}

        {dashboardName ? (
          <div style={s.infoBar}>
            <div><strong>Dashboard:</strong> {dashboardName}</div>
            <div><strong>Sheet:</strong> {sheetName}</div>
          </div>
        ) : null}

        {!presentationMode ? (
        <div style={s.panelCard}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div>
              <h3 style={s.panelTitle}>My Reports</h3>
              <p style={{ marginTop: 0, color: currentTheme.muted, fontSize: 13 }}>
                Saved reports stay here until you manually delete them. Use Refresh after a client opens a shared link to update view count.
              </p>
            </div>
            <button style={s.secondaryButton} onClick={fetchMyReports} disabled={reportsLoading}>
              {reportsLoading ? "Refreshing..." : "Refresh Reports"}
            </button>
          </div>

          {reportsLoading ? (
            <p>Loading reports...</p>
          ) : savedReports.length === 0 ? (
            <p>No saved reports yet.</p>
          ) : (
            <div style={{ display: "grid", gap: "10px" }}>
              {savedReports.map((report) => (
                <div
                  key={report.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "10px",
                    flexWrap: "wrap",
                    padding: "12px",
                    borderRadius: "12px",
                    border: `1px solid ${currentTheme.border}`,
                    background: currentTheme.secondaryBg,
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700 }}>{report.reportName}</div>
                    <div style={{ fontSize: "12px", color: currentTheme.muted }}>
                      {String(report.dashboardType || "").toUpperCase()} • {report.sheetName} • Rows: {report.totalRows}
                    </div>

                    <div style={{ fontSize: "12px", color: currentTheme.muted, marginTop: 6, lineHeight: 1.5 }}>
                      {report.share ? (
                        <>
                          <div><strong>Shared:</strong> Active until {report.share.expiresAt ? new Date(report.share.expiresAt).toLocaleString() : "No expiry"}</div>
                          <div><strong>Views:</strong> {report.share.viewCount || 0} {report.share.lastAccessedAt ? "• Last opened: " + new Date(report.share.lastAccessedAt).toLocaleString() : "• Not opened yet"}</div>
                        </>
                      ) : (
                        <div><strong>Shared:</strong> Not shared yet</div>
                      )}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                    <button style={s.secondaryButton} onClick={() => loadSavedReport(report.id)}>Load</button>
                    <button style={s.secondaryButton} onClick={() => deleteSavedReport(report.id)}>Delete</button>

                    <select
                      value={shareExpiry}
                      onChange={(e) => setShareExpiry(e.target.value)}
                      style={{ ...s.input, width: 110, marginBottom: 0, padding: "10px 12px" }}
                    >
                      <option value="24h">24 hours</option>
                      <option value="7d">7 days</option>
                    </select>

                    <button
                      style={{ ...s.secondaryButton, opacity: hasPremiumAccess ? 1 : 0.55, cursor: hasPremiumAccess ? "pointer" : "not-allowed" }}
                      onClick={() => generateShareLink(report.id)}
                      disabled={!hasPremiumAccess || shareLoadingId === report.id}
                      title={hasPremiumAccess ? "Generate client share link" : "Sharing is available only for paid users/internal team/admin"}
                    >
                      {shareLoadingId === report.id ? "Working..." : "Share Link"}
                    </button>

                    {report.share ? (
                      <>
                        <button
                          style={s.secondaryButton}
                          onClick={async () => {
                            if (report.share?.shareUrl) {
                              try {
                                if (navigator.clipboard && window.isSecureContext) {
                                  await navigator.clipboard.writeText(report.share.shareUrl);
                                  setMessage("Existing share link copied.");
                                } else {
                                  window.prompt("Copy this share link:", report.share.shareUrl);
                                  setMessage("Copy the share link from the popup box.");
                                }
                              } catch (copyError) {
                                window.prompt("Copy this share link:", report.share.shareUrl);
                                setMessage("Copy the share link from the popup box.");
                              }
                            }
                          }}
                        >
                          Copy Link
                        </button>

                        <button style={s.secondaryButton} onClick={() => revokeShareLink(report.id)} disabled={shareLoadingId === report.id}>
                          Disable Link
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        ) : null}


        {dashboardType ? (
          <>
            {!presentationMode ? (
            <div style={s.filterPanel}>
              <h3 style={s.panelTitle}>Filters</h3>
              <div style={s.filterGrid}>
                <input
                  style={s.input}
                  placeholder="Search anything..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />

                {Object.keys(optionSet).map((filterKey) => (
                  <select
                    key={filterKey}
                    style={s.input}
                    value={filters[filterKey] || ""}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        [filterKey]: e.target.value,
                      }))
                    }
                  >
                    <option value="">{filterKey}</option>
                    {optionSet[filterKey].map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                ))}
              </div>
            </div>
            ) : null}

            {!presentationMode ? renderAdvancedFeaturePanel() : null}

            <div style={s.tabRow}>
              <TabButton theme={currentTheme} active={activeTab === "story"} onClick={() => setActiveTab("story")}>
                Executive Story
              </TabButton>
              <TabButton theme={currentTheme} active={activeTab === "kpi"} onClick={() => setActiveTab("kpi")}>
                KPI Drivers
              </TabButton>
              <TabButton theme={currentTheme} active={activeTab === "builder"} onClick={() => setActiveTab("builder")}>
                Custom Dashboard Builder
              </TabButton>
              <TabButton theme={currentTheme} active={activeTab === "connected"} onClick={() => setActiveTab("connected")}>
                Connect Data
              </TabButton>
            </div>

            {activeTab !== "connected" ? renderHeroKpis() : null}
            {activeTab === "connected" ? renderConnectedDataSection() : renderVisibleTabSection()}

            <div ref={exportRef} style={s.exportVisibleArea}>
              {renderFullExportContent()}
            </div>
          </>
        ) : (
          <div style={s.emptyState}>Upload a file or load demo mode to begin.</div>
        )}
      </div>
    </div>
  );
}

function MiniKpi({ theme, title, value, status, trend }) {
  const s = styles(theme);
  const badge = status || { label: "Info", color: theme.accent || "#6366f1", bg: "rgba(99,102,241,0.12)" };
  return (
    <div style={{ ...s.kpiCard, border: `1px solid ${badge.color}` }}>
      <div style={s.kpiTitle}>{title}</div>
      <div style={{ ...s.kpiValue, color: badge.color }}>{value}</div>
      <div style={{ display: "inline-block", padding: "4px 8px", borderRadius: 999, background: badge.bg, color: badge.color, fontSize: 11, fontWeight: 900, marginTop: 8 }}>
        {badge.label}
      </div>
      {trend ? <div style={{ marginTop: 8, fontSize: 11, color: theme.muted }}>{trend}</div> : null}
    </div>
  );
}

function ChartCard({ theme, title, subtitle, children, compact = false }) {
  const s = styles(theme);
  return (
    <div style={{ ...s.chartCard, ...(compact ? s.compactChartCard : {}), breakInside: "avoid", pageBreakInside: "avoid" }}>
      <div style={s.chartHeader}>
        <div>
          <h3 style={s.chartTitle}>{title}</h3>
          {subtitle ? <p style={s.chartSubtitle}>{subtitle}</p> : null}
        </div>
        <div style={s.chartSignal}>LIVE</div>
      </div>
      <div style={{ ...s.chartWrap, ...(compact ? s.compactChartWrap : {}) }}>{children}</div>
    </div>
  );
}

function SectionCard({ theme, title, children }) {
  const s = styles(theme);
  return (
    <div style={{ ...s.sectionCard, breakInside: "avoid", pageBreakInside: "avoid" }}>
      <h3 style={s.sectionTitle}>{title}</h3>
      {children}
    </div>
  );
}

function TabButton({ theme, active, onClick, children }) {
  const s = styles(theme);
  return (
    <button
      style={{
        ...s.tabButton,
        ...(active ? s.tabButtonActive : {}),
      }}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

const darkTheme = {
  pageBackground: "#050816",
  appBackground: "radial-gradient(circle at 8% 10%, rgba(14,165,233,0.20) 0%, transparent 30%), radial-gradient(circle at 92% 0%, rgba(124,58,237,0.18) 0%, transparent 28%), linear-gradient(135deg, #050816 0%, #0b1220 48%, #020617 100%)",
  card: "rgba(11, 18, 32, 0.88)",
  elevated: "rgba(15, 23, 42, 0.78)",
  border: "rgba(148, 163, 184, 0.18)",
  text: "#e6eefc",
  heading: "#ffffff",
  muted: "#aab7cf",
  grid: "rgba(255,255,255,0.08)",
  inputBg: "#0f172a",
  infoBg: "rgba(7, 89, 133, 0.35)",
  infoBorder: "rgba(56, 189, 248, 0.25)",
  infoText: "#d7f1ff",
  primary: "linear-gradient(135deg, #6d5efc, #00c2ff)",
  secondaryBg: "#121d31",
  secondaryText: "#e7eefc",
  themeButton: "linear-gradient(135deg, #f59e0b, #ef4444)",
  chartLabel: "#ffffff",
  dataLabelBg: "rgba(15,23,42,0.8)",
  storyBg: "linear-gradient(135deg, rgba(99,102,241,0.18), rgba(6,182,212,0.10), rgba(16,185,129,0.07))",
};

const lightTheme = {
  pageBackground: "#f4f8fd",
  appBackground: "linear-gradient(180deg, #f8fbff, #eef5fc)",
  card: "#ffffff",
  elevated: "#ffffff",
  border: "rgba(15, 23, 42, 0.08)",
  text: "#1f2937",
  heading: "#111827",
  muted: "#5b6b84",
  grid: "rgba(15,23,42,0.08)",
  inputBg: "#ffffff",
  infoBg: "#e0f2fe",
  infoBorder: "#bae6fd",
  infoText: "#075985",
  primary: "linear-gradient(135deg, #2563eb, #06b6d4)",
  secondaryBg: "#f3f6fb",
  secondaryText: "#1f2937",
  themeButton: "linear-gradient(135deg, #111827, #374151)",
  chartLabel: "#111827",
  dataLabelBg: "rgba(255,255,255,0.85)",
  storyBg: "linear-gradient(180deg, rgba(37,99,235,0.10), rgba(6,182,212,0.06))",
};

const styles = (theme) => ({
  app: {
    minHeight: "100vh",
    background: theme.appBackground,
    color: theme.text,
    fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif",
    padding: "24px",
  },
  container: {
    maxWidth: "1560px",
    margin: "0 auto",
  },
  hero: {
    display: "flex",
    justifyContent: "space-between",
    gap: "16px",
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: "18px",
    padding: "24px",
    borderRadius: "28px",
    background: theme.elevated,
    border: `1px solid ${theme.border}`,
    boxShadow: "0 24px 70px rgba(0,0,0,0.18)",
  },
  heroLeft: {
    flex: 1,
    minWidth: "280px",
  },
  heroRight: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },
  logoWrap: {
    display: "flex",
    gap: "16px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  logo: {
    width: "66px",
    height: "66px",
    objectFit: "contain",
    borderRadius: "14px",
    background: "#ffffff",
    padding: "6px",
  },
  title: {
    margin: 0,
    fontSize: "clamp(28px, 4vw, 44px)",
    fontWeight: 800,
    color: theme.heading,
    lineHeight: 1.1,
  },
  subtitle: {
    margin: "8px 0 0 0",
    color: theme.muted,
    fontSize: "15px",
  },
  topPanel: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "16px",
    marginBottom: "16px",
  },
  panelCard: {
    background: theme.card,
    border: `1px solid ${theme.border}`,
    borderRadius: "24px",
    padding: "18px",
    boxShadow: "0 18px 48px rgba(15,23,42,0.14)",
    marginBottom: "16px",
  },
  panelTitle: {
    marginTop: 0,
    marginBottom: "12px",
    color: theme.heading,
    fontSize: "18px",
  },
  buttonGroup: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
  },
  input: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: "12px",
    border: `1px solid ${theme.border}`,
    background: theme.inputBg,
    color: theme.text,
    outline: "none",
    marginBottom: "10px",
  },
  primaryButton: {
    padding: "12px 16px",
    borderRadius: "12px",
    border: "none",
    background: theme.primary,
    color: "#ffffff",
    fontWeight: 700,
    cursor: "pointer",
    width: "100%",
  },
  secondaryButton: {
    padding: "10px 14px",
    borderRadius: "12px",
    border: `1px solid ${theme.border}`,
    background: theme.secondaryBg,
    color: theme.secondaryText,
    fontWeight: 700,
    cursor: "pointer",
  },
  demoButton: {
    padding: "10px 16px",
    borderRadius: "12px",
    border: "none",
    background: theme.primary,
    color: "#ffffff",
    fontWeight: 700,
    cursor: "pointer",
  },
  themeButton: {
    padding: "10px 16px",
    borderRadius: "12px",
    border: "none",
    background: theme.themeButton,
    color: "#ffffff",
    fontWeight: 700,
    cursor: "pointer",
  },
  message: {
    background: theme.infoBg,
    color: theme.infoText,
    padding: "12px 16px",
    borderRadius: "12px",
    marginBottom: "16px",
    border: `1px solid ${theme.infoBorder}`,
  },
  infoBar: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "10px",
    background: theme.card,
    borderRadius: "16px",
    padding: "14px",
    marginBottom: "16px",
    border: `1px solid ${theme.border}`,
  },
  filterPanel: {
    background: theme.card,
    borderRadius: "24px",
    padding: "18px",
    marginBottom: "16px",
    border: `1px solid ${theme.border}`,
    boxShadow: "0 18px 48px rgba(15,23,42,0.14)",
  },
  filterGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "10px",
  },
  tabRow: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
    marginBottom: "16px",
  },
  tabButton: {
    padding: "10px 18px",
    borderRadius: "999px",
    border: `1px solid ${theme.border}`,
    background: theme.secondaryBg,
    color: theme.text,
    cursor: "pointer",
    fontWeight: 700,
  },
  tabButtonActive: {
    background: theme.primary,
    color: "#ffffff",
  },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "12px",
    marginBottom: "18px",
  },
  kpiCard: {
    background: theme.card,
    border: `1px solid ${theme.border}`,
    borderRadius: "18px",
    padding: "16px",
    boxShadow: "0 8px 18px rgba(0,0,0,0.08)",
  },
  kpiTitle: {
    fontSize: "12px",
    fontWeight: 700,
    color: theme.muted,
    marginBottom: "8px",
  },
  kpiValue: {
    fontSize: "22px",
    fontWeight: 800,
    color: theme.heading,
    wordBreak: "break-word",
    lineHeight: 1.15,
  },
  sectionStack: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  sectionCard: {
    background: `linear-gradient(180deg, ${theme.card}, ${theme.elevated})`,
    border: `1px solid ${theme.border}`,
    borderRadius: "28px",
    padding: "22px",
    boxShadow: "0 24px 70px rgba(15,23,42,0.16)",
    backdropFilter: "blur(16px)",
  },
  sectionTitle: {
    margin: 0,
    color: theme.heading,
    fontSize: "20px",
    fontWeight: 900,
    letterSpacing: "-0.02em",
    marginBottom: "16px",
  },
  storyGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "16px",
  },
  storyCard: {
    background: theme.storyBg,
    border: `1px solid ${theme.border}`,
    borderRadius: "22px",
    padding: "18px",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06), 0 14px 34px rgba(15,23,42,0.10)",
  },
  storyTitle: {
    fontWeight: 800,
    color: theme.heading,
    marginBottom: "8px",
    fontSize: "15px",
  },
  storyText: {
    color: theme.text,
    lineHeight: 1.6,
    fontSize: "14px",
  },
  chartGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 430px), 1fr))",
    gap: "22px",
  },
  executiveVisualGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))",
    gap: "18px",
    alignItems: "stretch",
  },
  chartCard: {
    background: `linear-gradient(180deg, ${theme.card}, ${theme.elevated})`,
    border: `1px solid ${theme.border}`,
    borderRadius: "28px",
    padding: "20px",
    boxShadow: "0 24px 70px rgba(15,23,42,0.18)",
    overflow: "hidden",
    minHeight: "470px",
    position: "relative",
  },
  chartHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "14px",
    marginBottom: "12px",
    borderBottom: `1px solid ${theme.border}`,
    paddingBottom: "12px",
  },
  chartTitle: {
    margin: 0,
    color: theme.heading,
    fontSize: "18px",
    fontWeight: 900,
    letterSpacing: "-0.02em",
  },
  chartSubtitle: {
    margin: "6px 0 0 0",
    color: theme.muted,
    fontSize: "12px",
    lineHeight: 1.5,
  },
  chartSignal: {
    flexShrink: 0,
    fontSize: "10px",
    fontWeight: 900,
    letterSpacing: "0.08em",
    padding: "6px 9px",
    borderRadius: "999px",
    color: "#67e8f9",
    background: "rgba(6,182,212,0.12)",
    border: "1px solid rgba(103,232,249,0.20)",
  },
  chartWrap: {
    position: "relative",
    height: "360px",
    minHeight: "360px",
    width: "100%",
    overflowX: "auto",
    overflowY: "hidden",
    marginTop: "6px",
    paddingBottom: "4px",
  },
  compactChartCard: {
    minHeight: "390px",
    padding: "16px",
    borderRadius: "24px",
  },
  compactChartWrap: {
    height: "270px",
    minHeight: "270px",
    overflowX: "hidden",
  },
  emptyState: {
    background: theme.card,
    border: `1px dashed ${theme.border}`,
    borderRadius: "24px",
    padding: "42px",
    textAlign: "center",
    color: theme.muted,
  },
  exportVisibleArea: {
    position: "absolute",
    left: "-99999px",
    top: 0,
    width: "1200px",
    background: "#ffffff",
  },
  exportShell: {
    display: "flex",
    flexDirection: "column",
    gap: "18px",
  },
  exportHeader: {
    background: theme.card,
    border: `1px solid ${theme.border}`,
    borderRadius: "24px",
    padding: "18px",
  },
  exportTitle: {
    fontSize: "28px",
    fontWeight: 800,
    color: theme.heading,
    marginBottom: "6px",
  },
  exportSubtitle: {
    fontSize: "16px",
    fontWeight: 700,
    color: theme.text,
    marginBottom: "4px",
  },
  exportMeta: {
    fontSize: "13px",
    color: theme.muted,
  },
});

const authStyles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background:
      "radial-gradient(circle at top left, #dbeafe 0%, #f8fbff 35%, #eef4ff 100%)",
    padding: 24,
    fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif",
  },
  card: {
    width: "100%",
    maxWidth: 420,
    background: "#fff",
    borderRadius: 18,
    padding: 28,
    boxShadow: "0 16px 36px rgba(0,0,0,0.10)",
    border: "1px solid rgba(15,23,42,0.08)",
  },
  cardLarge: {
    width: "100%",
    maxWidth: 520,
    background: "#fff",
    borderRadius: 24,
    padding: 32,
    boxShadow: "0 20px 50px rgba(15,23,42,0.12)",
    border: "1px solid rgba(15,23,42,0.08)",
  },
  brandPill: {
    display: "inline-block",
    padding: "6px 12px",
    borderRadius: 999,
    background: "#eff6ff",
    color: "#1d4ed8",
    fontWeight: 700,
    fontSize: 12,
    marginBottom: 14,
  },
  title: {
    margin: 0,
    fontSize: 30,
    color: "#111827",
    fontWeight: 800,
  },
  sub: {
    marginTop: 10,
    marginBottom: 22,
    color: "#6b7280",
    lineHeight: 1.6,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  input: {
    padding: "14px 15px",
    borderRadius: 14,
    border: "1px solid #d1d5db",
    outline: "none",
    fontSize: 14,
    background: "#ffffff",
  },
  button: {
    padding: "14px 16px",
    borderRadius: 14,
    border: "none",
    background: "linear-gradient(135deg, #2563eb, #06b6d4)",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
    fontSize: 15,
  },
  secondaryButton: {
    padding: "13px 16px",
    borderRadius: 14,
    border: "1px solid #cbd5e1",
    background: "#0f172a",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
    fontSize: 14,
  },
  rowBetween: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  checkboxWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#475569",
    fontSize: 14,
  },
  textButton: {
    background: "transparent",
    border: "none",
    color: "#2563eb",
    cursor: "pointer",
    fontWeight: 700,
    padding: 0,
  },
  infoBox: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    padding: 14,
    marginTop: 4,
  },
  infoTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: "#0f172a",
    marginBottom: 6,
  },
  infoSub: {
    fontSize: 13,
    color: "#475569",
    lineHeight: 1.5,
  },
  msg: {
    marginTop: 14,
    color: "#111827",
    lineHeight: 1.5,
  },
  linkText: {
    marginTop: 18,
    color: "#4b5563",
  },
};

const navStyles = {
  wrap: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    padding: "14px 20px",
    borderBottom: "1px solid rgba(15,23,42,0.08)",
    background: "#ffffff",
    fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif",
  },
  title: {
    fontSize: 20,
    fontWeight: 800,
    color: "#111827",
  },
  sub: {
    fontSize: 13,
    color: "#6b7280",
    marginTop: 4,
  },
  links: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  link: {
    textDecoration: "none",
    color: "#2563eb",
    fontWeight: 700,
  },
  button: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "none",
    background: "#111827",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 700,
  },
};

function PublicSharedReportPage() {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const theme = lightTheme;
  const s = styles(theme);

  useEffect(() => {
    const fetchSharedReport = async () => {
      try {
        setLoading(true);
        setMessage("");
        const res = await API.get(`/public/reports/${token}`);
        if (res.data.success) {
          setReport(res.data.report);
          setShare(res.data.share || null);
        }
      } catch (error) {
        setMessage(error?.response?.data?.message || "This shared report is unavailable.");
      } finally {
        setLoading(false);
      }
    };
    fetchSharedReport();
  }, [token]);

  const rows = Array.isArray(report?.reportData) ? report.reportData : [];
  const dashboardType = String(report?.dashboardType || "").toLowerCase();
  const totalRows = rows.length;

  const numberValue = (value) => {
    const cleaned = String(value ?? "").replace(/[^0-9.\-]/g, "");
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : 0;
  };

  const formatCurrency = (num) => "$" + Math.round(Number(num || 0)).toLocaleString("en-US");
  const formatNumber = (num) => Number(num || 0).toLocaleString("en-US");
  const formatPercent = (num) => `${Number(num || 0).toLocaleString("en-US", { maximumFractionDigits: 1 })}%`;

  const groupByCount = (dataRows, key) => {
    const map = {};
    dataRows.forEach((row) => {
      const label = row[key] || "Unknown";
      map[label] = (map[label] || 0) + 1;
    });
    return map;
  };

  const groupBySum = (dataRows, groupKey, valueKeys) => {
    const keys = Array.isArray(valueKeys) ? valueKeys : [valueKeys];
    const map = {};
    dataRows.forEach((row) => {
      const label = row[groupKey] || "Unknown";
      const valueKey = keys.find((key) => row[key] !== undefined && row[key] !== "");
      map[label] = (map[label] || 0) + numberValue(valueKey ? row[valueKey] : 0);
    });
    return map;
  };

  const monthlySum = (dataRows, dateKey, valueKeys) => {
    const keys = Array.isArray(valueKeys) ? valueKeys : [valueKeys];
    const map = {};
    dataRows.forEach((row) => {
      const rawDate = row[dateKey];
      const date = rawDate ? new Date(rawDate) : null;
      if (!date || Number.isNaN(date.getTime())) return;
      const label = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      const valueKey = keys.find((key) => row[key] !== undefined && row[key] !== "");
      map[label] = (map[label] || 0) + numberValue(valueKey ? row[valueKey] : 0);
    });
    return map;
  };

  const topEntries = (obj, limit = 8) =>
    Object.entries(obj || {}).sort((a, b) => b[1] - a[1]).slice(0, limit);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    devicePixelRatio: 1,
    resizeDelay: 150,
    plugins: {
      legend: { position: "top", labels: { color: theme.text, font: { size: 12, weight: "700" } } },
      datalabels: {
        color: theme.chartLabel,
        backgroundColor: theme.dataLabelBg,
        borderRadius: 6,
        padding: 4,
        font: { size: 10, weight: "700" },
        formatter: (value) => (typeof value === "number" && Math.abs(value) >= 1000 ? `${Math.round(value / 1000)}K` : value),
      },
    },
    scales: {
      x: { ticks: { color: theme.muted, font: { size: 11, weight: "600" } }, grid: { color: theme.grid } },
      y: { ticks: { color: theme.muted, font: { size: 11, weight: "600" } }, grid: { color: theme.grid } },
    },
  };

  const lineOptions = {
    ...chartOptions,
    plugins: { ...chartOptions.plugins, datalabels: { display: false } },
  };

  const publicData = useMemo(() => {
    if (!rows.length) return null;

    if (dashboardType === "hr") {
      const employeesByCountryMap = groupByCount(rows, "Country");
      const departmentMap = groupByCount(rows, "Department");
      const workModeMap = groupByCount(rows, "Work_Mode");
      const experienceMap = groupByCount(rows, "Experience_Level");
      const demandRoleMap = groupBySum(rows, "Job_Title", "Applicants_Count");
      const salaryByDepartmentMap = groupBySum(rows, "Department", ["Salary_USD", "Salary"]);
      const totalEmployees = rows.length;
      const exitedEmployees = rows.filter((row) => String(row.Status || "").toLowerCase() === "exited").length;
      const activeEmployees = rows.filter((row) => String(row.Status || "").toLowerCase() === "active").length;
      const attritionRate = totalEmployees ? (exitedEmployees / totalEmployees) * 100 : 0;
      const avgSalary = totalEmployees ? rows.reduce((sum, row) => sum + numberValue(row.Salary_USD || row.Salary), 0) / totalEmployees : 0;
      const totalApplicants = rows.reduce((sum, row) => sum + numberValue(row.Applicants_Count), 0);
      const openPositions = rows.filter((row) => String(row.Job_Status || "").toLowerCase() === "open").length;
      const remoteCount = rows.filter((row) => String(row.Work_Mode || "").toLowerCase() === "remote").length;
      const remotePct = totalEmployees ? (remoteCount / totalEmployees) * 100 : 0;
      const topRole = topEntries(demandRoleMap, 1)[0]?.[0] || "-";
      const topCountry = topEntries(employeesByCountryMap, 1)[0]?.[0] || "-";
      const topDepartment = topEntries(departmentMap, 1)[0]?.[0] || "-";
      return {
        kpis: [["Total Employees", formatNumber(totalEmployees)], ["Active Employees", formatNumber(activeEmployees)], ["Attrition Rate", formatPercent(attritionRate)], ["Avg Salary", formatCurrency(avgSalary)], ["Open Positions", formatNumber(openPositions)], ["Total Applicants", formatNumber(totalApplicants)], ["Remote Workforce", formatPercent(remotePct)], ["Top Country", topCountry], ["Top Department", topDepartment], ["Highest Demand Role", topRole]],
        story: [["Executive Workforce View", `This report covers ${formatNumber(totalEmployees)} employees. ${topDepartment} is the largest department and ${topCountry} is the strongest location base, giving leadership a clear view of workforce concentration.`], ["Hiring Pressure", `${topRole} is the highest-demand role with strong applicant activity. This helps HR decide where to prioritize sourcing, interviews, and recruiter capacity.`], ["Retention Signal", `Attrition is currently ${formatPercent(attritionRate)}. If this number is high for your business target, leadership should review exit reasons, manager feedback, compensation, and role fit.`], ["Work Model Insight", `${formatPercent(remotePct)} of the workforce is remote. This is useful for planning office space, collaboration policy, hiring geography, and employee engagement strategy.`], ["Decision Recommendation", `Focus first on roles with high applicants and open positions, then compare salary and department mix to avoid hiring bottlenecks and retention risk.`]],
        charts: [{ type: "bar", title: "Employees by Country", map: employeesByCountryMap, label: "Employees", color: COLORS[0] }, { type: "bar", title: "Most In-Demand Roles", map: demandRoleMap, label: "Applicants", color: COLORS[2] }, { type: "doughnut", title: "Department Split", map: departmentMap, label: "Employees" }, { type: "doughnut", title: "Work Mode Distribution", map: workModeMap, label: "Employees" }, { type: "bar", title: "Salary Cost by Department", map: salaryByDepartmentMap, label: "Salary", color: COLORS[3] }, { type: "bar", title: "Experience Level Mix", map: experienceMap, label: "Employees", color: COLORS[5] }],
      };
    }

    if (dashboardType === "sales") {
      const revenueByRegionMap = groupBySum(rows, "Region", ["Revenue", "Net_Sales", "Gross_Sales"]);
      const topProductMap = groupBySum(rows, "Product_Name", ["Revenue", "Net_Sales", "Gross_Sales"]);
      const categoryMap = groupBySum(rows, "Product_Category", ["Revenue", "Net_Sales", "Gross_Sales"]);
      const salesByChannelMap = groupBySum(rows, "Channel", ["Revenue", "Net_Sales", "Gross_Sales"]);
      const profitByRepMap = groupBySum(rows, "Sales_Rep", "Profit");
      const revenueTrendMap = monthlySum(rows, "Order_Date", ["Revenue", "Net_Sales", "Gross_Sales"]);
      const orderStatusMap = groupByCount(rows, "Order_Status");
      const paymentStatusMap = groupByCount(rows, "Payment_Status");
      const totalRevenue = rows.reduce((sum, row) => sum + numberValue(row.Revenue || row.Net_Sales || row.Gross_Sales), 0);
      const totalProfit = rows.reduce((sum, row) => sum + numberValue(row.Profit), 0);
      const totalCost = rows.reduce((sum, row) => sum + numberValue(row.Cost), 0);
      const totalUnits = rows.reduce((sum, row) => sum + numberValue(row.Quantity || row.Units_Sold), 0);
      const profitMargin = totalRevenue ? (totalProfit / totalRevenue) * 100 : 0;
      const avgOrderValue = rows.length ? totalRevenue / rows.length : 0;
      const topProduct = topEntries(topProductMap, 1)[0]?.[0] || "-";
      const topRegion = topEntries(revenueByRegionMap, 1)[0]?.[0] || "-";
      const topChannel = topEntries(salesByChannelMap, 1)[0]?.[0] || "-";
      const topRep = topEntries(profitByRepMap, 1)[0]?.[0] || "-";
      return {
        kpis: [["Total Revenue", formatCurrency(totalRevenue)], ["Total Profit", formatCurrency(totalProfit)], ["Total Cost", formatCurrency(totalCost)], ["Profit Margin", formatPercent(profitMargin)], ["Total Orders", formatNumber(rows.length)], ["Units Sold", formatNumber(totalUnits)], ["Avg Order Value", formatCurrency(avgOrderValue)], ["Top Product", topProduct], ["Top Region", topRegion], ["Best Channel", topChannel], ["Top Sales Rep", topRep]],
        story: [["Revenue Performance", `The report shows ${formatCurrency(totalRevenue)} revenue across ${formatNumber(rows.length)} orders, with an average order value of ${formatCurrency(avgOrderValue)}. This gives leadership a quick view of sales scale and deal quality.`], ["Profit Quality", `Profit is ${formatCurrency(totalProfit)} with a ${formatPercent(profitMargin)} margin. If margin is below target, review discounting, product cost, and low-profit channels.`], ["Growth Driver", `${topProduct} is the strongest product and ${topRegion} is the strongest region. These areas can be prioritized for campaigns, delivery readiness, and sales focus.`], ["Channel Decision", `${topChannel} is currently the best-performing channel. Stakeholders should compare this with acquisition cost before increasing budget.`], ["Sales Execution", `${topRep} is leading by profit contribution. Use this as a benchmark for coaching, territory planning, and sales process replication.`]],
        charts: [{ type: "bar", title: "Revenue by Region", map: revenueByRegionMap, label: "Revenue", color: COLORS[1] }, { type: "bar", title: "Top Products by Revenue", map: topProductMap, label: "Revenue", color: COLORS[0] }, { type: "bar", title: "Revenue by Product Category", map: categoryMap, label: "Revenue", color: COLORS[3] }, { type: "doughnut", title: "Sales by Channel", map: salesByChannelMap, label: "Revenue" }, { type: "line", title: "Monthly Revenue Trend", map: revenueTrendMap, label: "Revenue", color: COLORS[6] }, { type: "bar", title: "Profit by Sales Rep", map: profitByRepMap, label: "Profit", color: COLORS[4] }, { type: "doughnut", title: "Order Status", map: orderStatusMap, label: "Orders" }, { type: "doughnut", title: "Payment Status", map: paymentStatusMap, label: "Orders" }],
      };
    }


    // public marketing view
    if (dashboardType === "marketing") {
      const totalSpend = rows.reduce((sum, row) => sum + numberValue(row.Spend), 0);
      const totalRevenue = rows.reduce((sum, row) => sum + numberValue(row.Revenue), 0);
      const totalLeads = rows.reduce((sum, row) => sum + numberValue(row.Leads), 0);
      const totalConversions = rows.reduce((sum, row) => sum + numberValue(row.Conversions), 0);
      const totalImpressions = rows.reduce((sum, row) => sum + numberValue(row.Impressions), 0);
      const totalClicks = rows.reduce((sum, row) => sum + numberValue(row.Clicks), 0);
      const roi = totalSpend ? ((totalRevenue - totalSpend) / totalSpend) * 100 : 0;
      const costPerLead = totalLeads ? totalSpend / totalLeads : 0;
      const ctr = totalImpressions ? (totalClicks / totalImpressions) * 100 : 0;
      const conversionRate = totalLeads ? (totalConversions / totalLeads) * 100 : 0;
      const revenueBySourceMap = groupBySum(rows, "Source", "Revenue");
      const leadsBySourceMap = groupBySum(rows, "Source", "Leads");
      const spendByChannelMap = groupBySum(rows, "Channel", "Spend");
      const conversionsByCampaignMap = groupBySum(rows, "Campaign_Name", "Conversions");
      const revenueTrendMap = monthlySum(rows, "Date", "Revenue");
      const topSource = topEntries(revenueBySourceMap, 1)[0]?.[0] || "-";
      const topCampaign = topEntries(conversionsByCampaignMap, 1)[0]?.[0] || "-";
      return {
        kpis: [["Revenue", formatCurrency(totalRevenue)], ["Spend", formatCurrency(totalSpend)], ["ROI", formatPercent(roi)], ["Leads", formatNumber(totalLeads)], ["Conversions", formatNumber(totalConversions)], ["CTR", formatPercent(ctr)], ["Conversion Rate", formatPercent(conversionRate)], ["Cost / Lead", formatCurrency(costPerLead)], ["Top Source", topSource], ["Top Campaign", topCampaign]],
        story: [["Marketing Overview", `This report shows ${formatCurrency(totalRevenue)} revenue from ${formatCurrency(totalSpend)} marketing spend, giving a clear ROI of ${formatPercent(roi)}.`], ["Lead Engine", `${formatNumber(totalLeads)} leads and ${formatNumber(totalConversions)} conversions were generated. Cost per lead is ${formatCurrency(costPerLead)}, which should be compared with target CPL.`], ["Best Source", `${topSource} is the strongest revenue source, making it a priority for budget allocation and audience expansion.`], ["Campaign Decision", `${topCampaign} leads conversions. Use its messaging and targeting as a benchmark for future campaigns.`]],
        charts: [{ type: "bar", title: "Revenue by Source", map: revenueBySourceMap, label: "Revenue", color: COLORS[1] }, { type: "bar", title: "Leads by Source", map: leadsBySourceMap, label: "Leads", color: COLORS[0] }, { type: "doughnut", title: "Spend by Channel", map: spendByChannelMap, label: "Spend" }, { type: "bar", title: "Conversions by Campaign", map: conversionsByCampaignMap, label: "Conversions", color: COLORS[3] }, { type: "line", title: "Revenue Trend", map: revenueTrendMap, label: "Revenue", color: COLORS[6] }],
      };
    }

    const expenseByCategoryMap = groupBySum(rows, "Category", "Expense");
    const profitByDepartmentMap = groupBySum(rows, "Department", "Profit");
    const actualTrendMap = monthlySum(rows, "Date", "Actual");
    const revenueByDepartmentMap = groupBySum(rows, "Department", "Revenue");
    const budgetByDepartmentMap = groupBySum(rows, "Department", "Budget");
    const expenseByRegionMap = groupBySum(rows, "Region", "Expense");
    const paymentStatusMap = groupByCount(rows, "Payment_Status");
    const accountTypeMap = groupByCount(rows, "Account_Type");
    const revenue = rows.reduce((sum, row) => sum + numberValue(row.Revenue), 0);
    const expense = rows.reduce((sum, row) => sum + numberValue(row.Expense), 0);
    const profit = rows.reduce((sum, row) => sum + numberValue(row.Profit), 0);
    const budget = rows.reduce((sum, row) => sum + numberValue(row.Budget), 0);
    const actual = rows.reduce((sum, row) => sum + numberValue(row.Actual), 0);
    const variance = actual - budget;
    const cashFlow = revenue - expense;
    const margin = revenue ? (profit / revenue) * 100 : 0;
    const largestExpense = topEntries(expenseByCategoryMap, 1)[0]?.[0] || "-";
    const bestDepartment = topEntries(profitByDepartmentMap, 1)[0]?.[0] || "-";
    return {
      kpis: [["Total Revenue", formatCurrency(revenue)], ["Total Expense", formatCurrency(expense)], ["Net Profit", formatCurrency(profit)], ["Cash Flow", formatCurrency(cashFlow)], ["Profit Margin", formatPercent(margin)], ["Budget", formatCurrency(budget)], ["Actual", formatCurrency(actual)], ["Variance", formatCurrency(variance)], ["Largest Expense", largestExpense], ["Best Department", bestDepartment]],
      story: [["Financial Health", `The report shows ${formatCurrency(revenue)} revenue, ${formatCurrency(expense)} expenses, and ${formatCurrency(profit)} net profit. This gives leaders a quick understanding of operating health.`], ["Cash Flow Signal", `Cash flow is ${formatCurrency(cashFlow)}. Positive cash flow supports growth investment, while weak cash flow means expense control and collections should be reviewed.`], ["Budget Control", `Budget vs Actual is ${formatCurrency(budget)} vs ${formatCurrency(actual)}, creating a variance of ${formatCurrency(variance)}. This helps identify whether teams are overspending or underutilizing planned budgets.`], ["Cost Priority", `${largestExpense} is the largest expense category. Stakeholders should review this area first for savings, vendor negotiation, or process improvement.`], ["Performance Driver", `${bestDepartment} is the strongest profit contributor. This department can be used as a performance benchmark for other teams.`]],
      charts: [{ type: "bar", title: "Expense by Category", map: expenseByCategoryMap, label: "Expense", color: COLORS[7] }, { type: "bar", title: "Profit by Department", map: profitByDepartmentMap, label: "Profit", color: COLORS[2] }, { type: "bar", title: "Revenue by Department", map: revenueByDepartmentMap, label: "Revenue", color: COLORS[1] }, { type: "bar", title: "Budget by Department", map: budgetByDepartmentMap, label: "Budget", color: COLORS[3] }, { type: "bar", title: "Expense by Region", map: expenseByRegionMap, label: "Expense", color: COLORS[4] }, { type: "line", title: "Actual Trend", map: actualTrendMap, label: "Actual", color: COLORS[5] }, { type: "doughnut", title: "Payment Status", map: paymentStatusMap, label: "Transactions" }, { type: "doughnut", title: "Account Type", map: accountTypeMap, label: "Transactions" }],
    };
  }, [rows, dashboardType]);

  const stakeholderActions = useMemo(() => {
    if (!publicData) return [];

    if (dashboardType === "hr") {
      return [
        ["Hiring Priority", "Focus recruitment energy on the highest-demand roles and open positions first. This reduces hiring delay and protects business continuity."],
        ["Retention Watch", "Use attrition and active employee signals to review exit reasons, manager feedback, salary alignment, and role-fit risks."],
        ["Workforce Planning", "Use country, department, work mode, and experience mix to decide hiring location, office planning, and team capacity."],
        ["Cost Alignment", "Compare salary cost by department with hiring demand so workforce investment stays aligned with business growth."],
      ];
    }

    if (dashboardType === "sales") {
      return [
        ["Revenue Focus", "Double down on the strongest product, region, and sales channel because these areas are already proving market demand."],
        ["Margin Protection", "Review profit margin, discounts, product cost, and low-profit orders before increasing sales spend."],
        ["Sales Execution", "Use the top-performing sales rep and best channel as a benchmark for coaching, campaign planning, and territory strategy."],
        ["Cash Discipline", "Check order and payment status to reduce pending payments, delayed collections, and revenue leakage."],
      ];
    }

    if (dashboardType === "marketing") {
      return [
        ["Budget Allocation", "Move spend toward high-ROI sources and campaigns instead of spreading budget equally across all channels."],
        ["Lead Quality", "Compare leads, conversions, and cost per lead together before scaling a campaign."],
        ["Campaign Testing", "Use top-performing messaging as the benchmark for landing pages, creatives, and targeting."],
        ["Revenue Discipline", "Protect ROI by cutting channels with weak conversion despite high clicks."],
      ];
    }

return [
      ["Cost Control", "Start with the largest expense category and highest-spend regions to identify savings opportunities and vendor negotiation areas."],
      ["Budget Governance", "Review budget vs actual variance to understand whether teams are overspending, underutilizing budgets, or planning inaccurately."],
      ["Cash Flow Priority", "Use cash flow and profit margin together before approving new investments, hiring, or expansion decisions."],
      ["Performance Benchmark", "Use the best-performing department as a benchmark for cost control, revenue quality, and operational discipline."],
    ];
  }, [publicData, dashboardType]);


  const aiInsights = useMemo(() => {
    if (!publicData) return [];

    if (dashboardType === "hr") {
      const kpis = Object.fromEntries(publicData.kpis || []);
      return [
        ["AI Insight: Workforce Risk", `The workforce view shows ${kpis["Attrition Rate"] || "-"} attrition and ${kpis["Open Positions"] || "-"} open positions. Leaders should check whether hiring speed and employee retention are moving in the same direction.`],
        ["AI Insight: Hiring Priority", `${kpis["Most Demanded Role"] || kpis["Top Role Demand"] || "The highest-demand role"} should be treated as the first hiring priority because demand concentration can slow delivery if not addressed quickly.`],
        ["AI Recommendation", "Create a retention + hiring action plan: interview exited employees, benchmark salary bands, speed up hiring for priority roles, and review remote/hybrid productivity before adding new locations."],
      ];
    }

    if (dashboardType === "sales") {
      const kpis = Object.fromEntries(publicData.kpis || []);
      return [
        ["AI Insight: Growth Focus", `${kpis["Top Product"] || "The top product"} and ${kpis["Top Region"] || "the leading region"} are the strongest business signals. Use them for targeted campaigns, upsell planning, and service readiness.`],
        ["AI Insight: Margin Check", `The report shows ${kpis["Profit Margin"] || "profit margin"}. Before pushing more sales volume, check discounts, cost, and low-margin orders so growth does not reduce profit quality.`],
        ["AI Recommendation", "Focus next action on the best region, protect margin through discount control, and create a recovery plan for pending payments or weak channels."],
      ];
    }

    if (dashboardType === "marketing") {
      const kpis = Object.fromEntries(publicData.kpis || []);
      return [
        ["AI Insight: ROI Signal", `Marketing ROI is ${kpis["ROI"] || "-"}. Use this to decide whether to increase or cut campaign spend.`],
        ["AI Insight: Lead Efficiency", `Cost per lead is ${kpis["Cost / Lead"] || "-"}. Compare this with conversion rate before scaling budget.`],
        ["AI Recommendation", "Prioritize the top source, improve low-converting campaigns, and test messaging where clicks are high but conversions are weak."],
      ];
    }

const kpis = Object.fromEntries(publicData.kpis || []);
    return [
      ["AI Insight: Financial Stability", `Cash flow is ${kpis["Cash Flow"] || "-"} and net profit is ${kpis["Net Profit"] || "-"}. These two numbers should guide whether the business can safely invest, hire, or expand.`],
      ["AI Insight: Cost Control", `${kpis["Largest Expense"] || "The largest expense category"} is the first area to review for cost saving, vendor negotiation, or unnecessary spending.`],
      ["AI Recommendation", "Review budget variance monthly, control the largest cost category, and replicate the practices of the best-performing department."],
    ];
  }, [publicData, dashboardType]);

  const renderPublicChart = (chart) => {
    const entries = chart.type === "line" ? Object.entries(chart.map || {}).sort() : topEntries(chart.map, 8);
    const labels = entries.map(([key]) => key);
    const data = entries.map(([, value]) => value);
    if (chart.type === "doughnut") {
      return <Doughnut data={{ labels, datasets: [{ label: chart.label, data, backgroundColor: COLORS }] }} options={{ ...chartOptions, scales: undefined, plugins: { ...chartOptions.plugins, legend: { position: "right", labels: { color: theme.text } } } }} />;
    }
    if (chart.type === "line") {
      return <Line data={{ labels, datasets: [{ label: chart.label, data, borderColor: chart.color, backgroundColor: chart.color, tension: 0.35 }] }} options={lineOptions} />;
    }
    return <Bar data={{ labels, datasets: [{ label: chart.label, data, backgroundColor: chart.color || COLORS[0], borderRadius: 8 }] }} options={chartOptions} />;
  };

  return (
    <div style={{ ...s.page, minHeight: "100vh", background: theme.pageBackground }}>
      <div style={{ maxWidth: 1220, margin: "0 auto" }}>
        <div style={{ ...s.hero, marginBottom: 18 }}>
          <div>
            <div style={s.badge}>READ ONLY PRESENTATION MODE</div>
            <h1 style={s.heroTitle}>{report?.reportName || "Bivora Smart Analytics"}</h1>
            <p style={s.heroSubtitle}>Client-ready presentation dashboard with KPI metrics, executive story, stakeholder decision guidance, and decision-focused charts. Editing, upload, save, delete, and export actions are disabled.</p>
          </div>
        </div>

        {loading ? <div style={s.message}>Loading shared report...</div> : null}
        {message ? <div style={s.message}>{message}</div> : null}

        {!loading && report && publicData ? (
          <>
            <div style={s.infoBar}>
              <div><strong>Type:</strong> {String(dashboardType).toUpperCase()}</div>
              <div><strong>Sheet:</strong> {report.sheetName || "-"}</div>
              <div><strong>Rows:</strong> {formatNumber(totalRows)}</div>
              {share?.expiresAt ? <div><strong>Expires:</strong> {new Date(share.expiresAt).toLocaleString()}</div> : null}
            </div>
            <div style={s.kpiGrid}>{publicData.kpis.map(([title, value]) => <MiniKpi key={title} theme={theme} title={title} value={value} />)}</div>
            <SectionCard theme={theme} title="Executive Summary"><div style={s.storyGrid}>{publicData.story.map(([title, text]) => <div key={title} style={s.storyCard}><div style={s.storyTitle}>{title}</div><div style={s.storyText}>{text}</div></div>)}</div></SectionCard>
            <SectionCard theme={theme} title="Stakeholder Decision Guide"><div style={s.storyGrid}>{stakeholderActions.map(([title, text]) => <div key={title} style={s.storyCard}><div style={s.storyTitle}>{title}</div><div style={s.storyText}>{text}</div></div>)}</div></SectionCard>
            <SectionCard theme={theme} title="AI Insights & Recommended Actions"><div style={s.storyGrid}>{aiInsights.map(([title, text]) => <div key={title} style={s.storyCard}><div style={s.storyTitle}>{title}</div><div style={s.storyText}>{text}</div></div>)}</div></SectionCard>
            <div style={s.chartGrid}>{publicData.charts.map((chart) => <ChartCard key={chart.title} theme={theme} title={chart.title}>{renderPublicChart(chart)}</ChartCard>)}</div>

          </>
        ) : null}
      </div>
    </div>
  );
}

function AdminUsersPage() {
  return (
    <div style={{ minHeight: "100vh", background: "#020617", color: "#e2e8f0" }}>
      <TopNav title="User Access" />
      <div style={{ maxWidth: 1480, margin: "0 auto", padding: "20px 20px 32px" }}>
        <div style={{ marginBottom: 16, padding: "16px 18px", borderRadius: 18, background: "#0f172a", border: "1px solid #334155" }}>
          <h1 style={{ margin: 0, color: "#f8fafc", fontSize: 26, fontWeight: 900 }}>User Access Management</h1>
          <p style={{ margin: "6px 0 0", color: "#cbd5e1", lineHeight: 1.6 }}>Manage users, roles, subscription access, and account controls clearly from this page.</p>
        </div>
        <AdminUsersPanel />
      </div>
    </div>
  );
}

function AdminLeadsPage() {
  return (
    <div style={{ minHeight: "100vh", background: "#020617", color: "#e2e8f0" }}>
      <TopNav title="Enterprise Leads" />
      <div style={{ maxWidth: 1480, margin: "0 auto", padding: "20px 20px 32px" }}>
        <div style={{ marginBottom: 16, padding: "16px 18px", borderRadius: 18, background: "#0f172a", border: "1px solid #334155" }}>
          <h1 style={{ margin: 0, color: "#f8fafc", fontSize: 26, fontWeight: 900 }}>Enterprise Leads</h1>
          <p style={{ margin: "6px 0 0", color: "#cbd5e1", lineHeight: 1.6 }}>Review enterprise inquiries and lead details with improved contrast and readability.</p>
        </div>
        <AdminEnterpriseLeads />
      </div>
    </div>
  );
}


export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/share/:token" element={<PublicSharedReportPage />} />
        <Route path="/" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <ClientDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute role="admin">
              <AdminDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute role="admin">
              <AdminUsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/leads"
          element={
            <ProtectedRoute role="admin">
              <AdminLeadsPage />
            </ProtectedRoute>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}